import { useEffect, useMemo, useState } from "react";
import { ColumnDef, PaginationState } from "@tanstack/react-table";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { Loader, MoreVertical, PlusCircle, Calendar as CalendarIcon, Printer, Upload, UserPlus, FileSpreadsheet, Users, Wallet, ShieldCheck, AlertTriangle, Landmark, AlertCircle, Receipt, CalendarDays, Download, DollarSign, Search, Eye, Mail, Phone, IdCard, X } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import DataTable from "@/components/datatable/Datatable";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useModalState from "@/hooks/useModalState";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getDefaultAvatar } from "@/utils/getDefaultAvatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/context/auth.context";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { can } from "@/hooks/usePermissions";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate, useParams } from "react-router-dom";
import * as z from "zod";
import useConfirmModal from "@/hooks/useConfirmModal";
import ConfirmModal from "@/components/modal/ConfirmModal";
import TransferRoleModal from "@/components/modal/TransferRoleModal";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import BulkImport from "@/components/modal/BulkImport";
import { Card, CardContent } from "@/components/ui/card";
import { getCooperativeLogoSrc } from "@/components/ui/cooperative-logo";
import { getShares } from "@/utils/shares";
import PaymentModal from "@/components/loans/PaymentModal";
import type { PaymentFormData } from "@/components/loans/PaymentModal";

const phoneRegex = /^(\+2507[28]\d{7}|07[28]\d{7})$/;
const formSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  role: z.enum(["President", "Secretary", "Accountant", "Member"], {
    required_error: "Role is required",
  }),
  gender: z.enum(["Male", "Female", "Other"]),
  marriageStatus: z.enum(["Single", "Married", "Divorced", "Widowed"]),
  idNumber: z.string().min(1, "ID number is required"),
  phone: z.string().regex(phoneRegex, "Enter a valid Rwanda phone (e.g. 078xxxxxxx or +25078xxxxxxx)"),
  email: z.string().email("Invalid email address"),
  country: z.string().optional(),
  currentAddress: z.string().optional(),
  sourceOfIncome: z.enum(["Employment", "Business", "Farming", "Freelance", "Other"]),
  joinedAt: z.string().min(1, "Join date is required"),
  sharesAmount: z.coerce.number().min(0, "Must be 0 or more").optional().default(0),
  unpaidLoansAndFines: z.coerce.number().min(0, "Must be 0 or more").optional().default(0),
  notes: z.string().optional(),
  password: z.string().min(6, "Password must be at least 6 characters").optional(),
});

function HistoryTable({ title, headers, rows, emptyText }) {
  return (
    <div className="rounded-lg border bg-white overflow-hidden">
      <div className="border-b px-3 py-2">
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">{emptyText}</div>
      ) : (
        <div className="max-h-[360px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/60">
              <tr className="border-b">
                {headers.map((header) => (
                  <th key={header} className="px-3 py-2 text-left font-medium last:text-right">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b last:border-b-0">
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className="px-3 py-2 last:text-right">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ActivityTimeline({ activities, contributions, loans, member }) {
  const allActivities = useMemo(() => {
    const items = [];

    // Member created
    if (member?.createdAt) {
      items.push({
        type: "created",
        description: "Member account created",
        date: member.createdAt,
        color: "bg-blue-500",
      });
    }

    // Status changes
    if (member?.statusChangedAt) {
      items.push({
        type: "status",
        description: `Status changed to ${member.membershipStatus || member.status}`,
        date: member.statusChangedAt,
        color: "bg-amber-500",
      });
    }

    // Contributions
    if (contributions && contributions.length > 0) {
      contributions.slice(0, 20).forEach((c) => {
        const date = c.contributionDate || c.salaryMonth || c.createdAt;
        if (date) {
          const status = c.status || (c.isVerified !== false ? "Verified" : "Pending");
          items.push({
            type: "contribution",
            description: `${status} contribution of ${(Number(c.amount || c.depositAmount) || 0).toLocaleString()} FRW`,
            date,
            color: status === "Verified" ? "bg-emerald-500" : "bg-orange-400",
          });
        }
      });
    }

    // Loans
    if (loans && loans.length > 0) {
      loans.slice(0, 20).forEach((l) => {
        const date = l.applicationDate || l.createdAt || l.date;
        if (date) {
          items.push({
            type: "loan",
            description: `Loan request: ${(Number(l.amount) || 0).toLocaleString()} FRW (${l.status || "Pending"})`,
            date,
            color: "bg-indigo-500",
          });
        }
      });
    }

    // Sort by date descending
    items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return items;
  }, [contributions, loans, member]);

  if (allActivities.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No activity recorded yet.
      </div>
    );
  }

  return (
    <div className="px-4 py-3">
      <div className="space-y-0">
        {allActivities.slice(0, 30).map((activity, idx) => (
          <div key={idx} className="relative flex items-start gap-3 pb-3">
            {/* Timeline line */}
            {idx < allActivities.length - 1 && (
              <div className="absolute left-[5px] top-3 h-full w-px bg-gray-200" />
            )}
            {/* Dot */}
            <div className={`relative mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${activity.color}`} />
            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-800">{activity.description}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {new Date(activity.date).toLocaleDateString()} at {new Date(activity.date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MemberForm({ isOpen, setIsOpen, refetch, record }) {
  const extractRole = (record: any) => {
    if (!record) return "Member";
    const role = record.leadershipRole ||
      record.role ||
      (record.leaderRoles && record.leaderRoles.length > 0 ? record.leaderRoles[0].role : undefined);
    return role || "Member";
  };

  const [contributionAmount, setContributionAmount] = useState(0);
  const [contributionDate, setContributionDate] = useState(new Date().toISOString().split("T")[0]);
  const [contributionMonth, setContributionMonth] = useState(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`);
  const [existingContributionId, setExistingContributionId] = useState<number | null>(null);
  const [contribLoading, setContribLoading] = useState(false);
  const [historicalContributions, setHistoricalContributions] = useState<Array<{month: string; amount: number; date: string}>>([]);
  const [newContributions, setNewContributions] = useState<Array<{month: string; amount: number; date: string}>>([]);
  const [showAllContributions, setShowAllContributions] = useState(false);
  const [modalMonth, setModalMonth] = useState(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`);
  const [modalAmount, setModalAmount] = useState<number>(0);
  const [modalDate, setModalDate] = useState(new Date().toISOString().split("T")[0]);

  // Transfer role modal state
  const [transferConflict, setTransferConflict] = useState<{
    role: string;
    currentHolderName: string;
    currentHolderId: number;
    currentHolderEmail: string;
  } | null>(null);
  const [transferLoading, setTransferLoading] = useState(false);

  useEffect(() => {
    if (isOpen && record?.id) {
      setContribLoading(true);
      setTransferConflict(null);
      setHistoricalContributions([]);
      setNewContributions([]);
      const now = new Date();
      const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      api.get(`/contributions?memberId=${record.id}&limit=50&sortBy=contributionDate&order=DESC`)
        .then((res) => {
          const items = res.data?.results || res.data?.data || [];
          if (items.length > 0) {
            // Set the latest contribution for editing
            const c = items[0];
            setExistingContributionId(c.id);
            setContributionAmount(c.amount || 0);
            setContributionDate(c.contributionDate ? c.contributionDate.split("T")[0] : defaultMonth);
            const d = c.salaryMonth || c.contributionDate || defaultMonth;
            const m = new Date(d);
            setContributionMonth(`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`);
            
            // Set historical contributions (skip the first one as it's being edited)
            if (items.length > 1) {
              const historical = items.slice(1).map((c: any) => {
                const d = c.salaryMonth || c.contributionDate || defaultMonth;
                const m = new Date(d);
                return {
                  month: `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`,
                  amount: c.amount || 0,
                  date: c.contributionDate ? c.contributionDate.split("T")[0] : defaultMonth,
                };
              });
              setHistoricalContributions(historical);
            }
          } else {
            setExistingContributionId(null);
            setContributionAmount(0);
            setContributionDate(defaultMonth);
            setContributionMonth(defaultMonth);
          }
        })
        .catch(() => { })
        .finally(() => setContribLoading(false));
    } else if (!isOpen) {
      setExistingContributionId(null);
      setContributionAmount(0);
      setHistoricalContributions([]);
      setNewContributions([]);
    }
  }, [isOpen, record?.id]);

  const addNewContributionRow = () => {
    const now = new Date();
    setNewContributions([...newContributions, {
      month: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
      amount: 0,
      date: now.toISOString().split("T")[0],
    }]);
  };

  const updateNewContribution = (index: number, field: string, value: string | number) => {
    const updated = [...newContributions];
    updated[index] = { ...updated[index], [field]: value };
    setNewContributions(updated);
  };

  const removeNewContribution = (index: number) => {
    setNewContributions(newContributions.filter((_, i) => i !== index));
  };

  const addContributionFromModal = async () => {
    if (!record?.id || modalAmount <= 0) return;
    
    try {
      await api.post("/contributions", {
        amount: modalAmount,
        contributionDate: modalDate,
        contributionType: "Monthly",
        salaryMonth: modalMonth + "-01",
        memberId: record.id,
        source: "salary_deduction",
        status: "Verified",
      });
      
      // Refresh contributions list
      const res = await api.get(`/contributions?memberId=${record.id}&limit=50&sortBy=contributionDate&order=DESC`);
      const items = res.data?.results || res.data?.data || [];
      const now = new Date();
      const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      
      if (items.length > 0) {
        const historical = items.map((c: any) => {
          const d = c.salaryMonth || c.contributionDate || defaultMonth;
          const m = new Date(d);
          return {
            month: `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`,
            amount: c.amount || 0,
            date: c.contributionDate ? c.contributionDate.split("T")[0] : defaultMonth,
          };
        });
        setHistoricalContributions(historical);
        
        // Update the main form with latest contribution
        const latest = items[0];
        setExistingContributionId(latest.id);
        setContributionAmount(latest.amount || 0);
        setContributionDate(latest.contributionDate ? latest.contributionDate.split("T")[0] : defaultMonth);
        const d = latest.salaryMonth || latest.contributionDate || defaultMonth;
        const m = new Date(d);
        setContributionMonth(`${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`);
      }
      
      // Reset modal form
      setModalAmount(0);
      setModalDate(new Date().toISOString().split("T")[0]);
      setModalMonth(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`);
      
      toast.success("Contribution added successfully");
      refetch();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to add contribution");
    }
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: record
      ? {
        ...record,
        role: extractRole(record),
        email: record?.email || "",
        sharesAmount: record?.savingAmount ?? record?.sharesTotalAmount ?? 0,
        unpaidLoansAndFines: record?.unpaidLoansAndFines ?? 0,
        notes: record?.notes ?? "",
      }
      : {
        firstName: "",
        lastName: "",
        gender: "",
        phone: "",
        marriageStatus: "",
        idNumber: "",
        country: "",
        currentAddress: "",
        joinedAt: new Date().toISOString().split("T")[0],
        sourceOfIncome: "",
        role: undefined,
        email: "",
        sharesAmount: 0,
        unpaidLoansAndFines: 0,
        notes: "",
      },
  });

  useEffect(() => {
    if (isOpen) {
      if (record) {
        const role = extractRole(record);
        if (role) {
          form.setValue("role", role);
        } else {
          form.setValue("role", undefined);
        }
      }
    }
  }, [isOpen, form, record]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      fullNames: `${values.firstName} ${values.lastName}`,
      gender: values.gender,
      phone: values.phone || "",
      marriageStatus: values.marriageStatus,
      idNumber: values.idNumber,
      country: values.country || "",
      currentAddress: values.currentAddress || "",
      joinedAt: values.joinedAt,
      sourceOfIncome: values.sourceOfIncome,
      memberCode: undefined,
      role: values.role,
      email: values.email,
      password: values.password || undefined,
      savingAmount: values.sharesAmount || 0,
      sharesTotalAmount: values.sharesAmount || 0,
      unpaidLoanAndFines: values.unpaidLoansAndFines || 0,
      notes: values.notes || "",
      membershipStatus: "Active",
    };
    const q = record
      ? api.patch(`/members/${record.id}`, payload)
      : api.post("/members", payload);

    return q
      .then(async () => {
        // Only create/update contribution from the Monthly Contribution form
        // The "Savings Amount" field only updates member.savingAmount, no separate contribution needed
        if (record?.id && contributionAmount > 0) {
          const contribPayload: any = {
            amount: contributionAmount,
            contributionDate: contributionDate,
            contributionType: "Monthly",
            salaryMonth: contributionMonth + "-01",
            memberId: record.id,
            source: "salary_deduction",
            status: "Verified",
          };
          // Check if this is an existing contribution or a new one
          if (existingContributionId) {
            await api.patch(`/contributions/${existingContributionId}`, contribPayload);
          } else {
            await api.post("/contributions", contribPayload);
          }
        }

        // Create new batch contributions
        if (record?.id && newContributions.length > 0) {
          for (const c of newContributions) {
            if (c.amount > 0) {
              try {
                await api.post("/contributions", {
                  amount: c.amount,
                  contributionDate: c.date,
                  contributionType: "Monthly",
                  salaryMonth: c.month + "-01",
                  memberId: record.id,
                  source: "salary_deduction",
                  status: "Verified",
                });
              } catch (err) {
                console.error("Failed to create contribution:", err);
              }
            }
          }
        }
        refetch();
        toast.success(record ? "Member updated successfully" : "Member created successfully");
        setIsOpen(false);
        form.reset();
        setNewContributions([]);
      })
      .catch((e) => {
        // Check for leader role conflict - show transfer modal instead of error
        const conflictData = e?.response?.data?.meta?.conflict;
        if (conflictData && e?.response?.data?.meta?.code === "LEADER_ROLE_CONFLICT") {
          setTransferConflict({
            role: conflictData.role,
            currentHolderName: conflictData.currentHolderName,
            currentHolderId: conflictData.currentHolderId,
            currentHolderEmail: conflictData.currentHolderEmail,
          });
          return;
        }
        toast.error(e.response?.data?.message || e.message);
        const errors = e?.response?.data?.meta?.errors || {};
        Object.keys(errors)?.forEach((field: any) => {
          form.setError(field, {
            message: errors[field],
          });
        });
      });
  }

  async function handleTransferConfirm() {
    if (!transferConflict || !record?.id) return;
    setTransferLoading(true);
    try {
      await api.post(`/members/${record.id}/transfer-role`, {
        targetRole: transferConflict.role,
        currentHolderId: transferConflict.currentHolderId,
      });
      toast.success(`${transferConflict.role} transferred successfully.`);
      setTransferConflict(null);
      refetch();
      setIsOpen(false);
      form.reset();
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to transfer role.");
    } finally {
      setTransferLoading(false);
    }
  }

  const { data: countries } = useQuery(["countries"], async () => {
    const data = await fetch("/countries.json")
      .then((e) => e.json())
      .then((e) => e.filter((e) => e.name.toLowerCase()))
      .then((e) => e.map((e) => ({ label: e.name, value: e.name })));
    return data;
  });

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="w-full flex flex-col !gap-0 sm:max-w-[720px] p-0">
        <SheetHeader className="sticky top-0 z-20 border-b bg-white px-6 py-4">
          <SheetTitle className="text-lg font-semibold text-gray-900">
            {record ? "Update Member" : "Add New Member"}
          </SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col h-full"
          >
            <ScrollArea className="flex-1 px-6 py-5">
              <div className="space-y-6">

                {/* Section 1: Member Information */}
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <h3 className="mb-3 text-sm font-semibold text-gray-700">Member Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField control={form.control} name="firstName" render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl><Input placeholder="Enter first name" error={fieldState?.error?.message} className="h-11 border-gray-300 focus:border-[#0A9F43] focus:ring-[#0A9F43] placeholder:text-gray-400" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="lastName" render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl><Input placeholder="Enter last name" error={fieldState?.error?.message} className="h-11 border-gray-300 focus:border-[#0A9F43] focus:ring-[#0A9F43] placeholder:text-gray-400" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="email" render={({ field, fieldState }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>Email *</FormLabel>
                        <FormControl><Input type="email" placeholder="member@example.com" error={fieldState?.error?.message} className="h-11 border-gray-300 focus:border-[#0A9F43] focus:ring-[#0A9F43] placeholder:text-gray-400" {...field} /></FormControl>
                        <FormMessage />
                        <p className="text-xs text-gray-500">Members use this email to log in</p>
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="gender" render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Gender</FormLabel>
                        <FormControl>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger error={fieldState?.error?.message} className="h-11 border-gray-300 focus:border-[#0A9F43] focus:ring-[#0A9F43]">
                                <SelectValue placeholder="Select gender" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {["Male", "Female", "Other"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="marriageStatus" render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Marriage Status</FormLabel>
                        <FormControl>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger error={fieldState?.error?.message} className="h-11 border-gray-300 focus:border-[#0A9F43] focus:ring-[#0A9F43]">
                                <SelectValue placeholder="Select marriage status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {["Single", "Married", "Divorced", "Widowed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="idNumber" render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>ID Number</FormLabel>
                        <FormControl><Input placeholder="Enter ID number" error={fieldState?.error?.message} type="number" className="h-11 border-gray-300 focus:border-[#0A9F43] focus:ring-[#0A9F43] placeholder:text-gray-400" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="phone" render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Phone *</FormLabel>
                        <FormControl><Input placeholder="078xxxxxxx" error={fieldState?.error?.message} className="h-11 border-gray-300 focus:border-[#0A9F43] focus:ring-[#0A9F43] placeholder:text-gray-400" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="role" render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Role *</FormLabel>
                        <FormControl>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger error={fieldState?.error?.message} className="h-11 border-gray-300 focus:border-[#0A9F43] focus:ring-[#0A9F43]">
                                <SelectValue placeholder="Select role" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="President">President</SelectItem>
                              <SelectItem value="Secretary">Secretary</SelectItem>
                              <SelectItem value="Accountant">Accountant</SelectItem>
                              <SelectItem value="Member">Member</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                </div>

                {/* Section 3: Financial Information */}
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <h3 className="mb-3 text-sm font-semibold text-gray-700">Financial Information</h3>
                  {record && (
                    <div className="mb-3 grid grid-cols-2 gap-3 rounded-lg bg-slate-50 p-3">
                      <div>
                        <p className="text-[10px] font-semibold uppercase text-slate-500">Total from Contributions</p>
                        <p className="text-sm font-bold text-slate-900">{(record.financialMetrics?.totalContributionAmount || 0).toLocaleString()} FRW</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase text-slate-500">Total from Savings</p>
                        <p className="text-sm font-bold text-[#0A9F43]">{(record.savingAmount ?? record.sharesTotalAmount ?? 0).toLocaleString()} FRW</p>
                      </div>
                      <div className="col-span-2 border-t border-slate-200 pt-2">
                        <p className="text-[10px] font-semibold uppercase text-slate-500">Combined Total</p>
                        <p className="text-base font-bold text-slate-900">{((record.savingAmount ?? record.sharesTotalAmount ?? 0) + (record.financialMetrics?.totalContributionAmount || 0)).toLocaleString()} FRW</p>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <FormField control={form.control} name="sharesAmount" render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Savings Amount (FRW)</FormLabel>
                        <FormControl><Input type="number" min="0" placeholder="0 FRW" error={fieldState?.error?.message} className="h-11 border-gray-300 focus:border-[#0A9F43] focus:ring-[#0A9F43] placeholder:text-gray-400" {...field} /></FormControl>
                        <p className="text-[10px] text-slate-500 mt-1">Increasing this amount creates a verified saving record visible to the member.</p>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="sourceOfIncome" render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Source of Income</FormLabel>
                        <FormControl>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger error={fieldState?.error?.message} className="h-11 border-gray-300 focus:border-[#0A9F43] focus:ring-[#0A9F43]">
                                <SelectValue placeholder="Select source" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {["Employment", "Business", "Farming", "Freelance", "Other"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="unpaidLoansAndFines" render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Unpaid Loan and Fines</FormLabel>
                        <FormControl><Input type="number" min="0" placeholder="0 FRW" error={fieldState?.error?.message} className="h-11 border-gray-300 focus:border-[#0A9F43] focus:ring-[#0A9F43] placeholder:text-gray-400" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="joinedAt" render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Join Date</FormLabel>
                        <FormControl><Input type="date" error={fieldState?.error?.message} className="h-11 border-gray-300 focus:border-[#0A9F43] focus:ring-[#0A9F43]" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                </div>

                {/* Section: Contribution */}
                {record && (
                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-700">Monthly Contribution</h3>
                      <button
                        type="button"
                        onClick={addNewContributionRow}
                        className="text-xs text-[#0A9F43] hover:text-[#0A9F43]/80 font-medium"
                      >
                        + Add More
                      </button>
                    </div>
                    {contribLoading ? (
                      <p className="text-xs text-slate-500 py-2">Loading...</p>
                    ) : (
                      <>
                        {/* Existing contributions list - show only 5 */}
                        {historicalContributions.length > 0 && (
                          <div className="mb-3 space-y-2">
                            <p className="text-xs font-medium text-slate-600">Previous Contributions:</p>
                            {historicalContributions.slice(0, 5).map((c, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs bg-slate-50 rounded p-2">
                                <span className="text-slate-500">{c.month}</span>
                                <span className="font-semibold text-slate-700">{Number(c.amount).toLocaleString()} FRW</span>
                                <span className="text-slate-400">({c.date})</span>
                              </div>
                            ))}
                            {historicalContributions.length > 5 && (
                              <button
                                type="button"
                                onClick={() => setShowAllContributions(true)}
                                className="text-xs text-[#0A9F43] hover:text-[#0A9F43]/80 font-medium"
                              >
                                View All ({historicalContributions.length} contributions)
                              </button>
                            )}
                          </div>
                        )}
                        
                        {/* Current contribution being edited */}
                        <div className="mb-3">
                          <p className="text-xs font-medium text-slate-600 mb-2">Current Month:</p>
                          <div className="flex items-end gap-3">
                            <div>
                              <label className="mb-1 block text-xs font-medium text-slate-600">Month</label>
                              <Input
                                type="month"
                                value={contributionMonth}
                                onChange={(e) => setContributionMonth(e.target.value)}
                                className="h-10 w-[160px] text-xs"
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-medium text-slate-600">Amount (FRW)</label>
                              <Input
                                type="number"
                                min="0"
                                placeholder="Enter amount"
                                value={contributionAmount || ""}
                                onChange={(e) => setContributionAmount(Number(e.target.value))}
                                className="h-10 w-[140px] text-xs"
                                onFocus={(e) => e.target.select()}
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-medium text-slate-600">Date</label>
                              <Input
                                type="date"
                                value={contributionDate}
                                onChange={(e) => setContributionDate(e.target.value)}
                                className="h-10 w-[150px] text-xs"
                              />
                            </div>
                            {existingContributionId && (
                              <p className="text-xs text-amber-600 pb-1">Editing existing</p>
                            )}
                          </div>
                        </div>

                        {/* New batch contributions */}
                        {newContributions.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-slate-600">Add Historical Contributions:</p>
                            {newContributions.map((c, idx) => (
                              <div key={idx} className="flex items-end gap-2 bg-blue-50 rounded p-2">
                                <div>
                                  <Input
                                    type="month"
                                    value={c.month}
                                    onChange={(e) => updateNewContribution(idx, "month", e.target.value)}
                                    className="h-9 w-[140px] text-xs"
                                  />
                                </div>
                                <div>
                                  <Input
                                    type="number"
                                    min="0"
                                    placeholder="Amount"
                                    value={c.amount || ""}
                                    onChange={(e) => updateNewContribution(idx, "amount", Number(e.target.value))}
                                    className="h-9 w-[120px] text-xs"
                                  />
                                </div>
                                <div>
                                  <Input
                                    type="date"
                                    value={c.date}
                                    onChange={(e) => updateNewContribution(idx, "date", e.target.value)}
                                    className="h-9 w-[130px] text-xs"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeNewContribution(idx)}
                                  className="text-red-500 hover:text-red-700 text-xs pb-1"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <p className="text-[10px] text-slate-400 mt-2">Add contributions from before using this platform by selecting past months</p>
                      </>
                    )}
                  </div>
                )}

                {/* Section 4: Address & Notes */}
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <h3 className="mb-3 text-sm font-semibold text-gray-700">Address & Notes</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField control={form.control} name="country" render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Country</FormLabel>
                        <FormControl>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger error={fieldState?.error?.message} className="h-11 border-gray-300 focus:border-[#0A9F43] focus:ring-[#0A9F43]">
                                <SelectValue placeholder="Select country" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {countries?.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <div></div>
                    <FormField control={form.control} name="currentAddress" render={({ field, fieldState }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>Address</FormLabel>
                        <FormControl><Textarea placeholder="Enter address" error={fieldState?.error?.message} className="min-h-[80px] border-gray-300 focus:border-[#0A9F43] focus:ring-[#0A9F43] placeholder:text-gray-400" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="notes" render={({ field, fieldState }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>Notes</FormLabel>
                        <FormControl><Textarea placeholder="Additional notes..." error={fieldState?.error?.message} className="min-h-[80px] border-gray-300 focus:border-[#0A9F43] focus:ring-[#0A9F43] placeholder:text-gray-400" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                </div>

              </div>
            </ScrollArea>
            <div className="sticky bottom-0 z-20 mt-auto flex items-center justify-end gap-3 border-t bg-white px-6 py-4">
              <Button type="button" variant="outline" size="lg" className="h-11 px-6" onClick={() => { setIsOpen(false); form.reset(); }}>
                Cancel
              </Button>
              <Button
                disabled={form.formState.disabled || form.formState.isSubmitting}
                type="submit"
                size="lg"
                className="h-11 bg-[#0A9F43] px-6 hover:bg-[#0A9F43]/90"
              >
                {form.formState.isSubmitting && <Loader className="mr-2 h-4 w-4 text-white animate-spin" />}
                {record ? "Update Member" : "Save Member"}
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
      <TransferRoleModal
        open={Boolean(transferConflict)}
        onClose={() => setTransferConflict(null)}
        onConfirm={handleTransferConfirm}
        isLoading={transferLoading}
        targetRole={transferConflict?.role || ""}
        currentHolderName={transferConflict?.currentHolderName || ""}
        newHolderName={record ? `${record.firstName} ${record.lastName}` : ""}
      />

      {/* All Contributions Modal */}
      <Dialog open={showAllContributions} onOpenChange={setShowAllContributions}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>All Contributions ({historicalContributions.length})</DialogTitle>
            <div className="text-sm font-medium text-[#0A9F43]">
              Total: {historicalContributions.reduce((sum, c) => sum + Number(c.amount), 0).toLocaleString()} FRW
            </div>
          </DialogHeader>
          
          {/* Add New Contribution Form */}
          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <h4 className="text-xs font-semibold text-slate-700 mb-3">Add New Contribution</h4>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-slate-600">Month</label>
                <Input
                  type="month"
                  value={modalMonth}
                  onChange={(e) => setModalMonth(e.target.value)}
                  className="h-10 text-xs"
                />
              </div>
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-slate-600">Amount (FRW)</label>
                <Input
                  type="number"
                  min="0"
                  placeholder="Enter amount"
                  value={modalAmount || ""}
                  onChange={(e) => setModalAmount(Number(e.target.value))}
                  className="h-10 text-xs"
                  onFocus={(e) => e.target.select()}
                />
              </div>
              <div className="flex-1">
                <label className="mb-1 block text-xs font-medium text-slate-600">Date</label>
                <Input
                  type="date"
                  value={modalDate}
                  onChange={(e) => setModalDate(e.target.value)}
                  className="h-10 text-xs"
                />
              </div>
              <Button
                type="button"
                onClick={addContributionFromModal}
                disabled={modalAmount <= 0}
                className="h-10 bg-[#0A9F43] hover:bg-[#0A9F43]/90 text-white"
              >
                Add
              </Button>
            </div>
          </div>

          {/* Contributions Table */}
          <div className="overflow-auto flex-1 border rounded-lg">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b bg-slate-50">
                  <th className="text-left p-3 font-semibold text-slate-600">#</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Month</th>
                  <th className="text-right p-3 font-semibold text-slate-600">Amount</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Date</th>
                </tr>
              </thead>
              <tbody>
                {historicalContributions.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-slate-400">
                      No contributions found
                    </td>
                  </tr>
                ) : (
                  historicalContributions.map((c, idx) => (
                    <tr key={idx} className="border-b hover:bg-slate-50 transition-colors">
                      <td className="p-3 text-slate-500">{idx + 1}</td>
                      <td className="p-3">
                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-medium">
                          {c.month}
                        </span>
                      </td>
                      <td className="p-3 text-right font-semibold text-slate-900">
                        {Number(c.amount).toLocaleString()} FRW
                      </td>
                      <td className="p-3 text-slate-500">{c.date}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {historicalContributions.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold">
                    <td className="p-3" colSpan={2}>Total ({historicalContributions.length} contributions)</td>
                    <td className="p-3 text-right text-[#0A9F43] text-sm">
                      {historicalContributions.reduce((sum, c) => sum + Number(c.amount), 0).toLocaleString()} FRW
                    </td>
                    <td className="p-3"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}


export default function Members() {
  const navigate = useNavigate();
  const { memberId } = useParams();
  const isDetailsPage = Boolean(memberId);
  const [recordToEdit, setRecordToEdit] = useState(undefined);
  const [searchText, setSearchText] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: undefined,
    to: undefined,
  });
  const [sorting, setSorting] = useState([
    {
      id: "memberCode",
      desc: false,
    },
  ]);

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const newRecordModal = useModalState();
  const confirmModal = useConfirmModal();
  const [selectedMember, setSelectedMember] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [memberHistoryYear, setMemberHistoryYear] = useState<string>("all");
  const [memberHistoryMonth, setMemberHistoryMonth] = useState<string>("all");
  const [contributionHistorySearch, setContributionHistorySearch] = useState("");
  const [loanHistorySearch, setLoanHistorySearch] = useState("");
  const [contributionHistoryFilters, setContributionHistoryFilters] = useState([]);
  const [loanHistoryFilters, setLoanHistoryFilters] = useState([]);
  const [contributionHistorySorting, setContributionHistorySorting] = useState([]);
  const [loanHistorySorting, setLoanHistorySorting] = useState([]);
  const [contributionHistoryPagination, setContributionHistoryPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [loanHistoryPagination, setLoanHistoryPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const { user } = useAuth();
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [memberToToggle, setMemberToToggle] = useState<{ id: number; name: string; currentStatus: string } | null>(null);
  const [memberToResetPassword, setMemberToResetPassword] = useState<{ id: number; name: string } | null>(null);
  const [toggleReason, setToggleReason] = useState("");
  const [toggleLoading, setToggleLoading] = useState(false);
  const [recordPaymentLoan, setRecordPaymentLoan] = useState<any>(null);
  const queryClient = useQueryClient();
  const activeMemberId = memberId || selectedMember?.id;
  // Sample row for template download
  const sampleMember = [{
    "Member Code": "4488",
    "Member Name": "BAHATI Innocent",
    "First Name": "BAHATI",
    "Last Name": "Innocent",
    "ID Number": "1199980012345678",
    "Phone": "079958348",
    "Email": "4488@ikimina.local",
    "Gender": "Male",
    "Marital Status": "Single",
    "Country": "Rwanda",
    "Current Address": "Rubavu",
    "Source of Income": "Business",
    "Joined Date": "2026-04-22"
  }];

  // Add separate queries for filter options
  const { data: branches = [] } = useQuery(["branches"], async () => {
    const { data } = await api.get("/branches");
    return data.results.map((branch) => ({
      label: branch.name,
      value: String(branch.id),
    }));
  });


  // Add gender options
  const genderOptions = [
    { label: "Male", value: "Male" },
    { label: "Female", value: "Female" },
    { label: "Other", value: "Other" }
  ];

  // Add marriage status options
  const marriageStatusOptions = [
    { label: "Single", value: "Single" },
    { label: "Married", value: "Married" },
    { label: "Divorced", value: "Divorced" },
    { label: "Widowed", value: "Widowed" }
  ];

  // Add source of income options
  const sourceOfIncomeOptions = [
    { label: "Employment", value: "Employment" },
    { label: "Business", value: "Business" },
    { label: "Farming", value: "Farming" },
    { label: "Freelance", value: "Freelance" },
    { label: "Other", value: "Other" }
  ];

  // Add a function to handle date range changes
  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range);
    if (range?.from && range?.to) {
      setColumnFilters(prev => {
        const otherFilters = prev.filter(f => f.id !== "createdAt");
        return [
          ...otherFilters,
          {
            id: "createdAt",
            value: [range.from, range.to]
          }
        ];
      });
    } else {
      setColumnFilters(prev => prev.filter(f => f.id !== "createdAt"));
    }
  };

  const recordsQuery = useQuery({
    queryKey: [
      "members",
      {
        search: searchText,
        filter: columnFilters,
        sort: sorting,
        pageIndex,
        pageSize,
      },
    ],
    keepPreviousData: true,
    queryFn: async () => {
      console.log("Current filters:", columnFilters);

      const fieldMap = {
        branch: "branchId",
        membershipStatus: "membershipStatus",
        createdAt: "createdAt",
      } as Record<string, string>;

      // "role" is derived from group memberships (not a direct DB column),
      // so it must be filtered client-side after the API response.
      const roleFilter = columnFilters.find((f) => f.id === "role");
      const apiFilters = columnFilters
        .filter((f) => f.id !== "role")
        .map((filter) => {
          if (filter.id === "createdAt" && filter.value?.length === 2) {
            const [startDate, endDate] = filter.value;
            if (!startDate || !endDate) {
              return null;
            }

            const endOfDay = new Date(endDate);
            endOfDay.setHours(23, 59, 59, 999);

            return {
              field: fieldMap[filter.id] || filter.id,
              operator: "between" as const,
              value: [
                new Date(startDate).toISOString(),
                endOfDay.toISOString(),
              ],
            };
          }

          if (!Array.isArray(filter.value) || filter.value.length === 0) {
            return null;
          }

          const normalizedValues = filter.value
            .map((v) => (typeof v === "object" && v !== null ? v.value : v))
            .map((v) => {
              if (filter.id === "branch") {
                const numericValue = Number(v);
                return Number.isNaN(numericValue) ? v : numericValue;
              }
              return v;
            })
            .filter((v) => v !== undefined && v !== null);

          if (normalizedValues.length === 0) {
            return null;
          }

          return {
            field: fieldMap[filter.id] || filter.id,
            operator: "in" as const,
            value: normalizedValues,
          };
        })
        .filter(Boolean);

      const params: Record<string, any> = {
        limit: pageSize,
        page: pageIndex + 1,
        ...(searchText && { search: searchText }),
        ...(apiFilters.length > 0 && { filters: JSON.stringify(apiFilters) }),
        sortBy: sorting[0]?.id || "createdAt",
        order: sorting[0]?.desc ? "DESC" : "ASC",
      };

      console.log("Final API params:", params);

      const { data } = await api.get(`/members`, { params });
      console.log("API Response:", data);

      const rawResults =
        data?.results ||
        data?.data?.results ||
        data?.data?.items ||
        data?.items ||
        (Array.isArray(data?.data) ? data.data : null) ||
        (Array.isArray(data) ? data : []);

      const items =
        rawResults?.map((e) => ({
          ...e,
          branchName: e?.branch?.name || "",
          branchId: e?.branch?.id,
          group:
            e?.groupMemberships && e.groupMemberships.length > 0
              ? e.groupMemberships[0]?.group?.name
              : "",
          createdAt: e?.createdAt,
          leaderRoles: e?.leaderRoles || [],
          groupMemberships: e?.groupMemberships || [],
          contributions: e?.contributions || [],
          loans: e?.loans || [],
          totalSharesBought: e?.totalSharesBought || 0,
          sharesTotalAmount: e?.savingAmount ?? e?.sharesTotalAmount ?? 0,
          unpaidLoansAndFines: e?.unpaidLoanAndFines ?? e?.unpaidLoansAndFines ?? 0,
          fullNames: e?.fullNames || `${e?.firstName} ${e?.lastName}`,
          currentSavings: e?.savingAmount ?? e?.currentSavings ?? 0,
          // New fields for the redesigned table - defensive fallback for shares
          totalShares: getShares(e?.totalShares, e?.totalSavings ?? e?.currentSavings ?? e?.savingAmount),
          totalSavings: e?.totalSavings ?? e?.savingAmount ?? 0,
          monthsSaved: e?.monthsSaved || 0,
          activeLoanBalance: e?.unpaidLoanAndFines ?? e?.activeLoanBalance ?? 0,
          outstandingFines: e?.outstandingFines || 0,
          status: e?.status || e?.membershipStatus || "Active",
          lastContributionDate: e?.lastContributionDate || null,
          activeLoanStatus: e?.activeLoanStatus || null,
        })) || [];

      // Client-side role filter: "role" is derived from group memberships
      // (leaderRoles[0].role or leadershipRole), not a direct DB column,
      // so the backend cannot filter it. Apply it here on the mapped items.
      let filteredItems = items;
      if (roleFilter?.value?.length) {
        const allowedRoles = roleFilter.value.map((v: any) =>
          typeof v === "object" && v !== null ? v.value : v
        );
        filteredItems = items.filter((item) => {
          const memberRole =
            item.leaderRoles?.[0]?.role ||
            item.leadershipRole ||
            item.role ||
            "Member";
          return allowedRoles.includes(memberRole);
        });
      }

      const totalCount = roleFilter?.value?.length
        ? filteredItems.length
        : (data?.total ??
          data?.data?.total ??
          data?.pagination?.total ??
          data?.data?.pagination?.total ??
          data?.count ??
          data?.data?.count ??
          items.length);
      const totalPages = roleFilter?.value?.length
        ? Math.ceil(filteredItems.length / pageSize)
        : (data?.totalPages ||
          data?.data?.totalPages ||
          data?.pagination?.totalPages ||
          data?.data?.pagination?.totalPages ||
          Math.ceil(totalCount / pageSize));

      return {
        items: filteredItems,
        totalPages,
        totalCount,
      };
    },
  });

  const members = recordsQuery.data?.items || [];
  const totalMatchingMembers = recordsQuery.data?.totalCount || 0;
  const activeMembersOnPage = members.filter(
    (member) => (member?.membershipStatus || "").toLowerCase() === "active"
  ).length;
  const totalSavingsOnPage = members.reduce(
    (sum, member) => sum + Number(member?.currentSavings || member?.totalSavings || 0),
    0
  );
  const unpaidExposureOnPage = members.reduce(
    (sum, member) => sum + Number(member?.unpaidLoansAndFines || 0),
    0
  );

  const formatCurrency = (value: number) =>
    `${value.toLocaleString()} FRW`;

  useEffect(() => {
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }, [searchText, columnFilters]);

  // No auto-selection — member must be explicitly selected by user

  // Real summary statistics from backend
  const statsQuery = useQuery(
    ["member-statistics"],
    async () => {
      const { data } = await api.get("/members/statistics");
      return data.data ?? data;
    },
    { staleTime: 30_000, retry: 1 }
  );
  const statsFailed = statsQuery.isError;
  const stats = statsQuery.data || {
    totalMembers: 0, maleCount: 0, femaleCount: 0,
    activeMembers: 0, newThisMonth: 0,
    totalSavings: 0, totalLoans: 0,
  };

  const pageTotalContributions = members.reduce(
    (sum, member) => sum + Number(member?.financialMetrics?.totalContributionAmount || 0),
    0
  );
  const pageTotalShares = members.reduce(
    (sum, member) => sum + Number(member?.totalShares || 0),
    0
  );
  const pageCombinedSavingsAndContributions = members.reduce(
    (sum, member) => {
      const savings = Number(member?.totalSavings || member?.currentSavings || 0);
      const contributions = Number(member?.financialMetrics?.totalContributionAmount || 0);
      return sum + savings + contributions;
    },
    0
  );

  const statsTotalSavings = Number(stats?.totalSavings || totalSavingsOnPage || 0);
  const statsTotalContributions = Number(stats?.totalContributions || pageTotalContributions || 0);
  const statsCombinedTotal = statsTotalSavings + statsTotalContributions;
  const statsTotalShares = Number(stats?.totalShares || pageTotalShares || 0);

  // Add query for member details
  const memberDetailsQuery = useQuery(
    ["member-details", activeMemberId],
    async () => {
      if (!activeMemberId) return null;
      const { data } = await api.get(`/members/${activeMemberId}`);
      return data.data;
    },
    {
      enabled: !!activeMemberId,
    }
  );

  const memberContributionsQuery = useQuery(
    ["member-contribution-history", activeMemberId],
    async () => {
      if (!activeMemberId) return [];
      const { data } = await api.get(`/contributions/teacher/${activeMemberId}`, {
        params: { limit: 1000 },
      });
      const items = data?.results || data?.data || data?.contributions || data || [];
      return Array.isArray(items) ? items : [];
    },
    {
      enabled: !!activeMemberId,
      retry: 1,
    }
  );

  const memberLoansQuery = useQuery(
    ["member-loan-history", activeMemberId],
    async () => {
      if (!activeMemberId) return [];
      const { data } = await api.get(`/loans/teacher/${activeMemberId}`);
      const items = data?.results || data?.data?.loans || data?.data || data?.loans || data || [];
      return Array.isArray(items) ? items : [];
    },
    {
      enabled: !!activeMemberId,
      retry: false,
    }
  );

  const recordPaymentMutation = useMutation(
    async ({ loanId, payload }: { loanId: number; payload: PaymentFormData }) => {
      const { data } = await api.post(`/loans/${loanId}/payments`, payload);
      return data;
    },
    {
      onSuccess: () => {
        toast.success("Payment recorded successfully");
        setRecordPaymentLoan(null);
        queryClient.invalidateQueries(["member-loan-history", activeMemberId]);
        queryClient.invalidateQueries(["member-details", activeMemberId]);
        queryClient.invalidateQueries({ queryKey: ["loans"], exact: false });
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.message || "Failed to record payment");
      },
    }
  );

  const memberContributionHistory = useMemo(() => {
    const detailsContributions = memberDetailsQuery.data?.contributions || [];
    const apiContributions = memberContributionsQuery.data || [];
    const byId = new Map<string, any>();

    [...detailsContributions, ...apiContributions].forEach((contribution: any, index: number) => {
      const key = contribution?.id?.toString() || `contribution-${index}`;
      byId.set(key, contribution);
    });

    return Array.from(byId.values()).sort((a: any, b: any) => {
      const aDate = new Date(a?.salaryMonth || a?.contributionDate || a?.createdAt || 0).getTime();
      const bDate = new Date(b?.salaryMonth || b?.contributionDate || b?.createdAt || 0).getTime();
      return bDate - aDate;
    });
  }, [memberDetailsQuery.data?.contributions, memberContributionsQuery.data]);

  const memberLoanHistory = useMemo(() => {
    const detailsLoans = memberDetailsQuery.data?.loans || [];
    const apiLoans = memberLoansQuery.data || [];
    const byId = new Map<string, any>();

    [...detailsLoans, ...apiLoans].forEach((loan: any, index: number) => {
      const key = loan?.id?.toString() || `loan-${index}`;
      byId.set(key, loan);
    });

    return Array.from(byId.values()).sort((a: any, b: any) => {
      const aDate = new Date(a?.applicationDate || a?.createdAt || a?.date || 0).getTime();
      const bDate = new Date(b?.applicationDate || b?.createdAt || b?.date || 0).getTime();
      return bDate - aDate;
    });
  }, [memberDetailsQuery.data?.loans, memberLoansQuery.data]);

  const memberAvailableHistoryYears = useMemo(() => {
    const years = new Set<number>();
    [...memberContributionHistory, ...memberLoanHistory].forEach((item: any) => {
      const dateValue = item?.salaryMonth || item?.contributionDate || item?.applicationDate || item?.createdAt || item?.date;
      const date = dateValue ? new Date(dateValue) : null;
      if (date && !Number.isNaN(date.getTime())) years.add(date.getFullYear());
    });
    const currentYear = new Date().getFullYear();
    for (let year = currentYear + 1; year >= 2000; year -= 1) years.add(year);
    return Array.from(years).sort((a, b) => b - a);
  }, [memberContributionHistory, memberLoanHistory]);

  const memberHistoryMonths = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const matchesMemberHistoryDate = (dateValue: any) => {
    const date = dateValue ? new Date(dateValue) : null;
    if (!date || Number.isNaN(date.getTime())) return memberHistoryYear === "all" && memberHistoryMonth === "all";
    const matchesYear = memberHistoryYear === "all" || date.getFullYear().toString() === memberHistoryYear;
    const matchesMonth = memberHistoryMonth === "all" || (date.getMonth() + 1).toString() === memberHistoryMonth;
    return matchesYear && matchesMonth;
  };

  const filteredMemberContributionHistory = memberContributionHistory.filter((contribution: any) => (
    matchesMemberHistoryDate(contribution?.salaryMonth || contribution?.contributionDate || contribution?.createdAt)
  ));

  const filteredMemberLoanHistory = memberLoanHistory.filter((loan: any) => (
    matchesMemberHistoryDate(loan?.applicationDate || loan?.createdAt || loan?.date)
  ));

  const memberContributionTotal = filteredMemberContributionHistory.reduce(
    (sum: number, contribution: any) => sum + Number(contribution?.amount || contribution?.depositAmount || 0),
    0
  );
  const memberContributionVerified = filteredMemberContributionHistory.filter((contribution: any) => (
    contribution?.status === "Verified" || contribution?.isVerified
  )).length;
  const memberLoanTotal = filteredMemberLoanHistory.reduce(
    (sum: number, loan: any) => sum + Number(loan?.amount || 0),
    0
  );
  const memberLoanPaid = filteredMemberLoanHistory.reduce(
    (sum: number, loan: any) => sum + Number(loan?.totalPaid || loan?.paidAmount || 0),
    0
  );
  const memberLoanBalance = memberLoanTotal - memberLoanPaid;

  useEffect(() => {
    setContributionHistoryPagination((current) => ({ ...current, pageIndex: 0 }));
  }, [contributionHistorySearch, contributionHistoryFilters, memberHistoryYear, memberHistoryMonth]);

  useEffect(() => {
    setLoanHistoryPagination((current) => ({ ...current, pageIndex: 0 }));
  }, [loanHistorySearch, loanHistoryFilters, memberHistoryYear, memberHistoryMonth]);

  const formatDate = (dateValue: any) => dateValue ? new Date(dateValue).toLocaleDateString() : "N/A";
  const matchesSearchText = (row: Record<string, any>, search: string) => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return Object.values(row).some((value) => String(value ?? "").toLowerCase().includes(query));
  };
  const paginateRows = (rows: any[], pagination: PaginationState) => {
    const start = pagination.pageIndex * pagination.pageSize;
    return rows.slice(start, start + pagination.pageSize);
  };
  const applyTableFilters = (rows: any[], filters: any[]) => {
    return rows.filter((row) => filters.every((filter) => {
      const values = Array.isArray(filter.value) ? filter.value : [];
      if (!values.length) return true;
      return values.includes(row[filter.id]);
    }));
  };
  const downloadCsv = (filename: string, rows: Record<string, any>[]) => {
    const headers = rows.length ? Object.keys(rows[0]) : [];
    const csv = [
      headers.join(","),
      ...rows.map((row) => headers.map((header) => {
        const value = String(row[header] ?? "");
        return `"${value.replace(/"/g, '""')}"`;
      }).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const contributionHistoryRows = filteredMemberContributionHistory.map((contribution: any) => {
    const dateValue = contribution?.salaryMonth || contribution?.contributionDate || contribution?.createdAt;
    const status = contribution?.status || (contribution?.isVerified ? "Verified" : "Pending");
    const amount = Number(contribution?.amount || contribution?.depositAmount || 0);
    return {
      date: formatDate(dateValue),
      type: contribution?.contributionType || "Contribution",
      method: contribution?.paymentMethod || "N/A",
      amount,
      amountLabel: formatCurrency(amount),
      status,
    };
  }).filter((row) => matchesSearchText(row, contributionHistorySearch));

  const loanHistoryRows = filteredMemberLoanHistory.map((loan: any) => {
    const dateValue = loan?.applicationDate || loan?.createdAt || loan?.date;
    const amount = Number(loan?.amount || 0);
    const paid = Number(loan?.totalPaid || loan?.paidAmount || 0);
    const balance = Number(loan?.dueAmount ?? Math.max(amount - paid, 0));
    return {
      date: formatDate(dateValue),
      type: loan?.loanType || loan?.type || "Loan",
      amount,
      amountLabel: formatCurrency(amount),
      paid,
      paidLabel: formatCurrency(paid),
      balance,
      balanceLabel: formatCurrency(balance),
      status: loan?.status || "Pending",
    };
  }).filter((row) => matchesSearchText(row, loanHistorySearch));

  const contributionHistoryColumns: ColumnDef<any>[] = [
    { accessorKey: "date", header: ({ column }) => <DataTableColumnHeader column={column} title="Date" /> },
    { accessorKey: "type", header: ({ column }) => <DataTableColumnHeader column={column} title="Type" /> },
    { accessorKey: "method", header: ({ column }) => <DataTableColumnHeader column={column} title="Method" /> },
    { accessorKey: "amountLabel", header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" /> },
    { accessorKey: "status", header: ({ column }) => <DataTableColumnHeader column={column} title="Status" /> },
  ];

  const loanHistoryColumns: ColumnDef<any>[] = [
    { accessorKey: "date", header: ({ column }) => <DataTableColumnHeader column={column} title="Date" /> },
    { accessorKey: "type", header: ({ column }) => <DataTableColumnHeader column={column} title="Type" /> },
    { accessorKey: "amountLabel", header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" /> },
    { accessorKey: "paidLabel", header: ({ column }) => <DataTableColumnHeader column={column} title="Paid" /> },
    { accessorKey: "balanceLabel", header: ({ column }) => <DataTableColumnHeader column={column} title="Balance" /> },
    { accessorKey: "status", header: ({ column }) => <DataTableColumnHeader column={column} title="Status" /> },
  ];

  const visibleContributionHistoryRows = applyTableFilters(contributionHistoryRows, contributionHistoryFilters);
  const visibleLoanHistoryRows = applyTableFilters(loanHistoryRows, loanHistoryFilters);
  const visibleAllHistoryRows = [
    ...visibleContributionHistoryRows.map((row) => ({
      category: "Contribution",
      date: row.date,
      type: row.type,
      method: row.method,
      amount: row.amountLabel,
      paid: "",
      balance: "",
      status: row.status,
      reason: "",
    })),
    ...visibleLoanHistoryRows.map((row) => ({
      category: "Loan",
      date: row.date,
      type: row.type,
      method: "",
      amount: row.amountLabel,
      paid: row.paidLabel,
      balance: row.balanceLabel,
      status: row.status,
      reason: "",
    })),
  ];

  // ── DataTable columns for Member Savings & Contributions ──
  const memberContributionColumns: ColumnDef<any, any>[] = [
    {
      accessorKey: "date",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
      cell: ({ row }) => {
        const d = row.original;
        const dateValue = d?.salaryMonth || d?.contributionDate || d?.createdAt;
        return (
          <div className="flex items-center gap-2">
            <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
            <span>{dateValue ? new Date(dateValue).toLocaleDateString() : "N/A"}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "contributionType",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
      cell: ({ row }) => <span className="text-xs">{row.original?.contributionType || "Contribution"}</span>,
    },
    {
      accessorKey: "paymentMethod",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Method" />,
      cell: ({ row }) => <span className="text-xs">{row.original?.paymentMethod || "—"}</span>,
    },
    {
      accessorKey: "amount",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
      cell: ({ row }) => {
        const amount = Number(row.original?.amount || row.original?.depositAmount || 0);
        return <span className="text-right font-semibold text-[#0A9F43]">{formatCurrency(amount)}</span>;
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => {
        const status = row.original?.status || (row.original?.isVerified !== false ? "Verified" : "Pending");
        return (
          <Badge variant="outline" className={`text-xs ${status === "Verified" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-orange-200 bg-orange-50 text-orange-700"
            }`}>{status}</Badge>
        );
      },
    },
  ];

  // ── DataTable columns for Member Loan & Fine History ──
  const memberLoanColumns: ColumnDef<any, any>[] = [
    {
      accessorKey: "date",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Date" />,
      cell: ({ row }) => {
        const d = row.original;
        const dateValue = d?.applicationDate || d?.createdAt || d?.date;
        return (
          <div className="flex items-center gap-2">
            <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
            <span>{dateValue ? new Date(dateValue).toLocaleDateString() : "N/A"}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "type",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
      cell: ({ row }) => <span className="text-xs">{row.original?.loanType || row.original?.type || "Loan"}</span>,
    },
    {
      accessorKey: "amount",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
      cell: ({ row }) => {
        const amount = Number(row.original?.amount || 0);
        return <span className="text-right font-semibold">{formatCurrency(amount)}</span>;
      },
    },
    {
      accessorKey: "paid",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Paid" />,
      cell: ({ row }) => {
        const paid = Number(row.original?.totalPaid || row.original?.paidAmount || 0);
        return <span className="text-right text-emerald-600">{formatCurrency(paid)}</span>;
      },
    },
    {
      accessorKey: "balance",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Balance" />,
      cell: ({ row }) => {
        const d = row.original;
        const amount = Number(d?.amount || 0);
        const paid = Number(d?.totalPaid || d?.paidAmount || 0);
        const balance = Number(d?.dueAmount ?? Math.max(amount - paid, 0));
        return (
          <span className={`text-right font-semibold ${balance > 0 ? "text-red-600" : "text-gray-400"}`}>
            {formatCurrency(balance)}
          </span>
        );
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => {
        const status = row.original?.status || "Pending";
        const isCompleted = String(status).toLowerCase() === "completed";
        return (
          <Badge variant="outline" className={`text-xs ${isCompleted ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-blue-200 bg-blue-50 text-blue-700"
            }`}>{status}</Badge>
        );
      },
    },
  ];

  // ── DataTable pagination for member detail tables ──
  const [memberContribPage, setMemberContribPage] = useState(0);
  const [memberContribPageSize, setMemberContribPageSize] = useState(10);
  const [memberLoanPage, setMemberLoanPage] = useState(0);
  const [memberLoanPageSize, setMemberLoanPageSize] = useState(10);
  const [memberContribSearch, setMemberContribSearch] = useState("");
  const [memberLoanSearch, setMemberLoanSearch] = useState("");

  const filteredMemberContributions = useMemo(() => {
    if (!memberContribSearch.trim()) return filteredMemberContributionHistory;
    const q = memberContribSearch.toLowerCase();
    return filteredMemberContributionHistory.filter((c: any) =>
      c?.contributionType?.toLowerCase().includes(q) ||
      c?.paymentMethod?.toLowerCase().includes(q) ||
      String(c?.amount || "").includes(q) ||
      (c?.status || "").toLowerCase().includes(q)
    );
  }, [filteredMemberContributionHistory, memberContribSearch]);

  const filteredMemberLoans = useMemo(() => {
    if (!memberLoanSearch.trim()) return filteredMemberLoanHistory;
    const q = memberLoanSearch.toLowerCase();
    return filteredMemberLoanHistory.filter((l: any) =>
      (l?.loanType || l?.type || "").toLowerCase().includes(q) ||
      String(l?.amount || "").includes(q) ||
      (l?.status || "").toLowerCase().includes(q)
    );
  }, [filteredMemberLoanHistory, memberLoanSearch]);

  const columns: ColumnDef<any>[] = [
    ...(user?.isAdmin ? [{
      id: "select",
      header: ({ table }) => (
        <Checkbox
          // @ts-ignore
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    }] : []),
    {
      accessorKey: "memberCode",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Member ID" />
      ),
      cell: ({ row }) => (
        <button
          onClick={() => setSelectedMember(row.original)}
          className="font-mono text-sm font-semibold text-slate-700 hover:text-[#0A9F43]"
        >
          {row.original.memberCode || `IKM-${String(row.original.id).padStart(4, "0")}`}
        </button>
      ),
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Member Name" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-3 min-w-[220px]">
            <Avatar className="h-8 w-8">
              <AvatarImage src={row.original.avatar || getDefaultAvatar(row.original.id || row.original.email)} />
              <AvatarFallback>
                {row.original.firstName?.[0]}
                {row.original.lastName?.[0]}
              </AvatarFallback>
            </Avatar>
            <div>
              <button
                onClick={() => setSelectedMember(row.original)}
                className="font-medium hover:underline text-left whitespace-nowrap"
              >
                {row.original.fullNames || `${row.original.firstName || ""} ${row.original.lastName || ""}`.trim()}
              </button>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "email",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Email" />
      ),
      cell: ({ row }) => (
        <div className="text-sm text-slate-600 min-w-[180px]">
          {row.getValue("email") || <span className="text-gray-400 text-xs">—</span>}
        </div>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "phone",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Phone" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-3 text-sm text-slate-600 min-w-[140px]">
            {row.getValue("phone") || <span className="text-gray-400 text-xs">—</span>}
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "monthsSaved",
      accessorKey: "monthsSaved",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Active Loan" />
      ),
      cell: ({ row }) => {
        const hasActiveLoan = Boolean(row.original.activeLoanStatus || Number(row.original.activeLoanBalance || 0) > 0);
        return (
          <span className={cn("text-xs font-bold", hasActiveLoan ? "text-blue-700" : "text-slate-500")}>
            {hasActiveLoan ? "Yes" : "No"}
          </span>
        );
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: "totalSavings",
      accessorKey: "totalSavings",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Total Savings" />
      ),
      cell: ({ row }) => {
        const savings = row.getValue("totalSavings") || 0;
        return (
          <span className="text-sm font-semibold text-[#0A9F43]">
            {Number(savings).toLocaleString()} FRW
          </span>
        );
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: "activeLoanBalance",
      accessorKey: "activeLoanBalance",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Total Contributions" />
      ),
      cell: ({ row }) => {
        const amount = Number(row.original.financialMetrics?.totalContributionAmount || 0);
        const balance = amount;
        const hasBalance = true;
        return (
          <span className="text-sm font-semibold text-slate-800">
            {hasBalance ? `${Number(balance).toLocaleString()} FRW` : "—"}
          </span>
        );
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      id: "combinedTotal",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Total (Savings + Contributions)" />
      ),
      cell: ({ row }) => {
        const savings = Number(row.original.totalSavings || row.original.currentSavings || 0);
        const contributions = Number(row.original.financialMetrics?.totalContributionAmount || 0);
        return (
          <span className="whitespace-nowrap text-sm font-bold text-slate-900">
            {(savings + contributions).toLocaleString()} FRW
          </span>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "role",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Role" />
      ),
      cell: ({ row }) => {
        const primaryRole =
          row.original.leaderRoles?.[0]?.role ||
          row.original.leadershipRole ||
          row.original.role ||
          "Member";
        const roleStyles: Record<string, string> = {
          President: "bg-[#0A9F43]/10 text-[#0A9F43] border-[#0A9F43]/30",
          Secretary: "bg-blue-100 text-blue-700 border-blue-200",
          Accountant: "bg-amber-100 text-amber-700 border-amber-200",
          Member: "bg-emerald-50 text-emerald-700 border-emerald-200",
        };
        return (
          <Badge
            variant="outline"
            className={`text-xs font-semibold px-2.5 py-0.5 rounded-md ${roleStyles[primaryRole] || roleStyles.Member
              }`}
          >
            {primaryRole === "Member" ? "Ordinary Member" : primaryRole}
          </Badge>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "membershipStatus",
      id: "membershipStatus",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const status = row.original.status || row.original.membershipStatus || "Active";
        const isActive = status.toLowerCase() === "active";
        return (
          <Badge
            variant="outline"
            className={`text-xs font-semibold px-2.5 py-0.5 rounded-md ${isActive
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-red-200 bg-red-50 text-red-600"
              }`}
          >
            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${isActive ? "bg-emerald-500" : "bg-red-400"}`} />
            {isActive ? "Active" : "Inactive"}
          </Badge>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "actions",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Actions" />
      ),
      cell: ({ row }) => (
        <div className="flex items-center justify-end mt-1 gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-7 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreVertical size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={() => {
                  navigate(`/members/${row.original.id}`);
                }}
              >
                View Profile
              </DropdownMenuItem>
              {can(user, "members.edit") && (
                <DropdownMenuItem
                  onClick={() => {
                    setRecordToEdit(row?.original);
                  }}
                >
                  Edit Member
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => {
                  navigate(`/members/${row.original.id}?tab=statement`);
                }}
              >
                View Statement
              </DropdownMenuItem>
              {can(user, "members.edit") && (
                <DropdownMenuItem
                  onClick={() => {
                    const currentStatus = row.original.status || row.original.membershipStatus || "Active";
                    const isCurrentlyActive = currentStatus.toLowerCase() === "active";
                    setMemberToToggle({
                      id: row.original.id,
                      name: `${row.original.firstName} ${row.original.lastName}`,
                      currentStatus: isCurrentlyActive ? "Active" : "Inactive",
                    });
                  }}
                  className={row.original.status?.toLowerCase() === "active" ? "text-orange-600 hover:bg-orange-50 focus:bg-orange-50" : "text-emerald-600 hover:bg-emerald-50 focus:bg-emerald-50"}
                >
                  {row.original.status?.toLowerCase() === "active" ? "Deactivate Member" : "Activate Member"}
                </DropdownMenuItem>
              )}
              {can(user, "members.edit") && (
                <DropdownMenuItem
                  onClick={() => {
                    setMemberToResetPassword({ id: row.original.id, name: `${row.original.firstName || ""} ${row.original.lastName || ""}`.trim() });
                  }}
                >
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Reset Password
                </DropdownMenuItem>
              )}
              {can(user, "members.delete") && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem
                        onClick={() => {
                          confirmModal.open({ meta: row?.original });
                        }}
                        className="text-red-600 hover:bg-red-50 focus:bg-red-50"
                      >
                        Delete
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    {(row.original.groupMemberships?.length > 0 ||
                      row.original.contributions?.length > 0 ||
                      row.original.loans?.length > 0 ||
                      row.original.currentSavings !== 0) && (
                        <TooltipContent>
                          <p>This member has active data. Deletion may fail.</p>
                        </TooltipContent>
                      )}
                  </Tooltip>
                </TooltipProvider>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  const handleDelete = (record) => {
    confirmModal.setIsLoading(true);
    return api
      .delete(`/members/${record.id}`)
      .then(() => {
        recordsQuery.refetch();
        confirmModal.close();
        toast.success("Member deleted successfully");
      })
      .catch((e) => {
        confirmModal.setIsLoading(false);
        const errorMessage = e.response?.data?.message || e.message;

        // Check if it's the specific constraint error
        if (errorMessage.includes("Cannot delete member with group memberships, contributions, or loans")) {
          toast.error("Cannot delete member. This member has active group memberships, contributions, or loans. Please remove these relationships first.");
        } else {
          toast.error(errorMessage);
        }
      });
  };

  const handleToggleStatus = async () => {
    if (!memberToToggle) return;
    setToggleLoading(true);
    try {
      const newStatus = memberToToggle.currentStatus === "Active" ? "inactive" : "active";
      await api.patch(`/members/${memberToToggle.id}/status`, {
        status: newStatus,
        reason: toggleReason || undefined,
      });
      toast.success(`Member ${newStatus === "active" ? "activated" : "deactivated"} successfully`);
      recordsQuery.refetch();
      setMemberToToggle(null);
      setToggleReason("");
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to update member status");
    } finally {
      setToggleLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!memberToResetPassword) return;
    try {
      await api.patch(`/members/${memberToResetPassword.id}`, { password: "123456" });
      toast.success(`Password reset to 123456 for ${memberToResetPassword.name}`);
      setMemberToResetPassword(null);
    } catch (e: any) {
      toast.error(e.response?.data?.message || "Failed to reset password");
    }
  };

  const panelMember = selectedMember ? (memberDetailsQuery.data || selectedMember) : null;
  const panelStatus = panelMember?.status || panelMember?.membershipStatus || "Active";
  const panelIsActive = String(panelStatus).toLowerCase() === "active";
  const panelSavings = Number(panelMember?.totalSavings || panelMember?.currentSavings || panelMember?.savingAmount || 0);
  const panelContributions = Number(panelMember?.financialMetrics?.totalContributionAmount || memberContributionTotal || 0);
  const panelCombinedTotal = panelSavings + panelContributions;
  const panelShares = Number(panelMember?.totalShares || 0);
  const panelTotalLoans = Number(panelMember?.financialMetrics?.totalLoansAmount || memberLoanTotal || 0);
  const panelLoansPaid = Number(panelMember?.financialMetrics?.totalLoansPaid || memberLoanPaid || 0);
  const panelLoanBalance = Number(panelMember?.activeLoanBalance || memberLoanBalance || 0);
  const panelActiveLoans = Number(panelMember?.financialMetrics?.activeLoansCount || filteredMemberLoanHistory.filter((loan: any) => {
    const status = String(loan?.status || "").toLowerCase().replace(/\s+/g, "_");
    return ["active", "defaulted", "approved", "disbursed", "overdue"].includes(status);
  }).length);
  const panelRole = panelMember?.leaderRoles?.[0]?.role || panelMember?.leadershipRole || panelMember?.role || "Member";
  const panelFullName = panelMember?.fullNames || `${panelMember?.firstName || ""} ${panelMember?.lastName || ""}`.trim();

  if (isDetailsPage) {
    const m = memberDetailsQuery.data;
    const isActive = m?.status === "active" || m?.membershipStatus === "Active";

    return (
      <div className="px-4 py-4 w-full max-w-full">
        {/* Header Bar */}
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/members")}>
              Back
            </Button>
            <h1 className="text-xl font-bold text-gray-900">Member Profile</h1>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => navigate(`/members/${m?.id}?tab=statement`)}
            >
              <Receipt className="h-3.5 w-3.5 mr-1" />
              Statement
            </Button>
            {can(user, "members.edit") && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setRecordToEdit(m)}
              >
                Edit
              </Button>
            )}
            {can(user, "members.edit") && (
              <Button
                variant="outline"
                size="sm"
                className={`h-7 px-2 text-xs ${isActive ? "border-orange-200 text-orange-600 hover:bg-orange-50" : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"}`}
                onClick={() => {
                  setMemberToToggle({
                    id: m.id,
                    name: m.fullNames,
                    currentStatus: isActive ? "Active" : "Inactive",
                  });
                }}
              >
                {isActive ? "Deactivate" : "Activate"}
              </Button>
            )}
          </div>
        </div>

        {memberDetailsQuery.isLoading ? (
          <div className="flex min-h-[420px] items-center justify-center rounded-lg border bg-white">
            <Loader className="h-6 w-6 animate-spin" />
          </div>
        ) : m ? (
          <div className="space-y-6">
            {/* Profile Header Card */}
            <div className="rounded-lg border bg-white p-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={m.avatar || getDefaultAvatar(m.id || m.email)} />
                  <AvatarFallback className="text-base font-semibold">
                    {m.firstName?.[0]}{m.lastName?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-bold text-gray-900">{m.fullNames}</h2>
                    <Badge variant="outline" className="text-xs font-mono">#{m.memberCode || m.id}</Badge>
                    <Badge
                      variant="outline"
                      className={`text-xs font-semibold px-2.5 py-0.5 rounded-md ${isActive
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-red-200 bg-red-50 text-red-600"
                        }`}
                    >
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${isActive ? "bg-emerald-500" : "bg-red-400"}`} />
                      {isActive ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">Phone: {m.phone || "—"}</span>
                    <span>|</span>
                    <span className="flex items-center gap-1">
                      Joined: {m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : "—"}
                    </span>
                    {m.leadershipRole && m.leadershipRole !== "Member" && (
                      <>
                        <span>|</span>
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                          {m.leadershipRole}
                        </Badge>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <div className="rounded-lg border bg-white p-3 relative overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-[#0A9F43] to-[#12B24B]" />
                <p className="text-[11px] text-muted-foreground font-medium">Total Savings</p>
                <p className="text-lg font-bold text-[#0A9F43] mt-0.5">{formatCurrency(m.totalSavings || 0)}</p>
              </div>
              <div className="rounded-lg border bg-white p-3 relative overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-400" />
                <p className="text-[11px] text-muted-foreground font-medium">Total Shares</p>
                <p className="text-lg font-bold text-blue-600 mt-0.5">{Number(m.totalShares || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} <span className="text-xs font-normal text-muted-foreground">sh</span></p>
              </div>
              <div className="rounded-lg border bg-white p-3 relative overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-violet-500 to-purple-400" />
                <p className="text-[11px] text-muted-foreground font-medium">Months Saved</p>
                <p className="text-lg font-bold text-violet-600 mt-0.5">{m.monthsSaved || 0} <span className="text-xs font-normal text-muted-foreground">months</span></p>
              </div>
              <div className="rounded-lg border bg-white p-3 relative overflow-hidden">
                <div className={`absolute inset-x-0 top-0 h-0.5 ${(m.activeLoanBalance || 0) > 0 ? "bg-gradient-to-r from-red-500 to-orange-400" : "bg-gradient-to-r from-gray-300 to-gray-200"}`} />
                <p className="text-[11px] text-muted-foreground font-medium">Loan Balance</p>
                <p className={`text-lg font-bold mt-0.5 ${(m.activeLoanBalance || 0) > 0 ? "text-red-600" : "text-gray-400"}`}>
                  {(m.activeLoanBalance || 0) > 0 ? formatCurrency(m.activeLoanBalance) : "—"}
                </p>
              </div>
              <div className="rounded-lg border bg-white p-3 relative overflow-hidden">
                <div className={`absolute inset-x-0 top-0 h-0.5 ${(m.outstandingFines || 0) > 0 ? "bg-gradient-to-r from-orange-500 to-amber-400" : "bg-gradient-to-r from-gray-300 to-gray-200"}`} />
                <p className="text-[11px] text-muted-foreground font-medium">Outstanding Fines</p>
                <p className={`text-lg font-bold mt-0.5 ${(m.outstandingFines || 0) > 0 ? "text-orange-600" : "text-gray-400"}`}>
                  {(m.outstandingFines || 0) > 0 ? formatCurrency(m.outstandingFines) : "—"}
                </p>
              </div>
              <div className="rounded-lg border bg-white p-3 relative overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-400" />
                <p className="text-[11px] text-muted-foreground font-medium">Last Contribution</p>
                <p className="text-sm font-semibold text-gray-800 mt-1">
                  {m.lastContributionDate ? new Date(m.lastContributionDate).toLocaleDateString() : "—"}
                </p>
              </div>
              <div className="rounded-lg border bg-white p-3 relative overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-amber-500 to-yellow-400" />
                <p className="text-[11px] text-muted-foreground font-medium">Loan Status</p>
                <p className="text-sm font-semibold text-gray-800 mt-1">
                  {m.activeLoanStatus || "No Active Loan"}
                </p>
              </div>
              <div className="rounded-lg border bg-white p-3 relative overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-cyan-500 to-sky-400" />
                <p className="text-[11px] text-muted-foreground font-medium">Net Savings</p>
                <p className={`text-lg font-bold mt-0.5 ${(m.financialMetrics?.netSavings || 0) >= 0 ? "text-cyan-600" : "text-red-600"}`}>
                  {formatCurrency(m.financialMetrics?.netSavings || 0)}
                </p>
              </div>
            </div>

            {/* Profile Information */}
            <div className="rounded-lg border bg-white overflow-hidden">
              <div className="border-b px-4 py-2.5">
                <h3 className="text-sm font-semibold text-gray-900">Profile Information</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2">
                {[
                  ["Gender", m.gender || "N/A"],
                  ["Marriage Status", m.marriageStatus || "N/A"],
                  ["Source of Income", m.sourceOfIncome || "N/A"],
                  ["Joined Date", m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : "N/A"],
                  ["Address", m.currentAddress || "N/A"],
                  ["District", m.branch?.district?.name || "—"],
                  ["ID Number", m.idNumber || "—"],
                  ["Email", m.email || "—"],
                  ["Country", m.country || "—"],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center border-b last:border-b-0 sm:[&:nth-last-child(2)]:border-b-0">
                    <span className="w-36 shrink-0 bg-muted/40 px-4 py-2.5 text-xs font-medium text-muted-foreground">{label}</span>
                    <span className="px-4 py-2.5 text-sm font-medium text-gray-900">{value}</span>
                  </div>
                ))}
                {m.leaderRoles && m.leaderRoles.length > 0 && (
                  <div className="flex items-center border-b last:border-b-0 sm:[&:nth-last-child(2)]:border-b-0">
                    <span className="w-36 shrink-0 bg-muted/40 px-4 py-2.5 text-xs font-medium text-muted-foreground">Leader Roles</span>
                    <span className="px-4 py-2.5 text-sm font-medium text-gray-900">
                      {m.leaderRoles.map((lr) => `${lr.role}${lr.group?.name ? ` in ${lr.group.name}` : ""}`).join(", ")}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Contribution History */}
            <div className="rounded-lg border bg-white overflow-hidden">
              <div className="border-b px-5 py-3">
                <h3 className="text-lg font-bold text-gray-900">Contribution History</h3>
                <p className="text-xs text-muted-foreground mt-0.5">View all salary deductions and manual contributions made by this member.</p>
              </div>
              <div className="p-0">
                <DataTable
                  columns={memberContributionColumns}
                  data={filteredMemberContributions}
                  isLoading={memberContributionsQuery.isLoading}
                  isFetching={memberContributionsQuery.isFetching}
                  pageIndex={memberContribPage}
                  pageSize={memberContribPageSize}
                  pageCount={Math.max(1, Math.ceil(filteredMemberContributions.length / memberContribPageSize))}
                  setPagination={(updater) => {
                    const ns = typeof updater === "function"
                      ? updater({ pageIndex: memberContribPage, pageSize: memberContribPageSize })
                      : updater;
                    setMemberContribPage(ns.pageIndex);
                    setMemberContribPageSize(ns.pageSize);
                  }}
                  onSearch={(v) => { setMemberContribSearch(v); setMemberContribPage(0); }}
                  enableClientFiltering
                  facets={[
                    {
                      name: "status",
                      title: "Status",
                      options: [
                        { label: "Verified", value: "Verified" },
                        { label: "Pending", value: "Pending" },
                      ],
                    },
                  ]}
                  sorting={[]}
                  setSorting={() => { }}
                  onExport={() => downloadCsv("member-contributions.csv", visibleContributionHistoryRows)}
                  emptyMessage="No contribution records found."
                  emptyDescription="Contributions will appear here once recorded."
                  hidePagination={false}
                  className="border-0 shadow-none"
                />
              </div>
            </div>

            {/* Loan History */}
            <div className="rounded-lg border bg-white overflow-hidden">
              <div className="border-b px-5 py-3">
                <h3 className="text-lg font-bold text-gray-900">Loan History</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Displays all loans, status, amount, balance, and repayment progress for this member.</p>
              </div>
              <div className="p-0">
                <DataTable
                  columns={memberLoanColumns}
                  data={filteredMemberLoans}
                  isLoading={memberLoansQuery.isLoading}
                  isFetching={memberLoansQuery.isFetching}
                  pageIndex={memberLoanPage}
                  pageSize={memberLoanPageSize}
                  pageCount={Math.max(1, Math.ceil(filteredMemberLoans.length / memberLoanPageSize))}
                  setPagination={(updater) => {
                    const ns = typeof updater === "function"
                      ? updater({ pageIndex: memberLoanPage, pageSize: memberLoanPageSize })
                      : updater;
                    setMemberLoanPage(ns.pageIndex);
                    setMemberLoanPageSize(ns.pageSize);
                  }}
                  onSearch={(v) => { setMemberLoanSearch(v); setMemberLoanPage(0); }}
                  enableClientFiltering
                  facets={[
                    {
                      name: "status",
                      title: "Status",
                      options: [
                        { label: "Active", value: "active" },
                        { label: "Completed", value: "completed" },
                        { label: "Pending", value: "Pending" },
                        { label: "Overdue", value: "overdue" },
                      ],
                    },
                  ]}
                  sorting={[]}
                  setSorting={() => { }}
                  onExport={() => downloadCsv("member-loans.csv", visibleLoanHistoryRows)}
                  emptyMessage="No loan records found."
                  emptyDescription="Loan history will appear here once available."
                  hidePagination={false}
                  className="border-0 shadow-none"
                />
              </div>
            </div>

            {/* Activity History */}
            <div className="rounded-lg border bg-white overflow-hidden">
              <div className="border-b px-5 py-3">
                <h3 className="text-lg font-bold text-gray-900">Activity History</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Recent actions and changes to this member's account.</p>
              </div>
              <div className="max-h-[360px] overflow-auto">
                <ActivityTimeline activities={[]} contributions={filteredMemberContributionHistory} loans={filteredMemberLoanHistory} member={m} />
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border bg-white p-8 text-center text-muted-foreground">No member details available</div>
        )}
      </div>
    );
  }

  return (
    <>

      <ConfirmModal
        title={"Are you sure you want to delete?"}
        description={`This will permanently delete the member and cannot be undone. Note: Members with group memberships, contributions, or loans cannot be deleted.`}
        meta={confirmModal.meta}
        onConfirm={(meta) => {
          handleDelete(meta);
        }}
        isLoading={confirmModal.isLoading}
        open={confirmModal.isOpen}
        onClose={() => confirmModal.close()}
      />

      {/* Activate/Deactivate Confirmation Dialog */}
      <Dialog open={Boolean(memberToToggle)} onOpenChange={(open) => { if (!open) { setMemberToToggle(null); setToggleReason(""); } }}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {memberToToggle?.currentStatus === "Active" ? (
                <AlertTriangle className="h-5 w-5 text-orange-500" />
              ) : (
                <ShieldCheck className="h-5 w-5 text-emerald-500" />
              )}
              {memberToToggle?.currentStatus === "Active" ? "Deactivate Member" : "Activate Member"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              {memberToToggle?.currentStatus === "Active"
                ? `Are you sure you want to deactivate ${memberToToggle?.name}? They will be prevented from loan applications, declarations, and payments.`
                : `Are you sure you want to activate ${memberToToggle?.name}? They will be restored to full access.`}
            </p>
            {memberToToggle?.currentStatus === "Active" && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Reason (optional)</label>
                <Textarea
                  placeholder="Enter reason for deactivation..."
                  value={toggleReason}
                  onChange={(e) => setToggleReason(e.target.value)}
                  className="min-h-[80px]"
                />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => { setMemberToToggle(null); setToggleReason(""); }}
              disabled={toggleLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleToggleStatus}
              disabled={toggleLoading}
              className={memberToToggle?.currentStatus === "Active" ? "bg-orange-600 hover:bg-orange-700" : "bg-[#0A9F43] hover:bg-[#0A9F43]/90"}
            >
              {toggleLoading && <Loader className="mr-2 h-4 w-4 animate-spin" />}
              {memberToToggle?.currentStatus === "Active" ? "Deactivate" : "Activate"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Confirmation Dialog */}
      <Dialog open={Boolean(memberToResetPassword)} onOpenChange={(open) => { if (!open) setMemberToResetPassword(null); }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-amber-500" />
              Reset Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Reset password for <strong>{memberToResetPassword?.name}</strong> to <strong>123456</strong>?
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setMemberToResetPassword(null)}>Cancel</Button>
            <Button onClick={handleResetPassword} className="bg-amber-600 hover:bg-amber-700">Reset to 123456</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-[96vw] w-[96vw] h-[92vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="border-b px-4 py-3">
            <DialogTitle className="text-lg font-semibold flex items-center gap-2">
              <span>Member Details</span>
              {memberDetailsQuery.data && (
                <Badge variant="outline" className="text-xs">
                  ID: #{memberDetailsQuery.data.id}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {memberDetailsQuery.isLoading ? (
            <div className="flex items-center justify-center p-6 h-full">
              <Loader className="h-6 w-6 animate-spin" />
            </div>
          ) : memberDetailsQuery.data ? (
            <ScrollArea className="flex-1">
              <div className="p-4">
                {/* Compact Header */}
                <div className="mb-4 bg-card rounded-lg border p-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={memberDetailsQuery.data.avatar || getDefaultAvatar(memberDetailsQuery.data.id || memberDetailsQuery.data.email)} />
                      <AvatarFallback className="text-sm">
                        {memberDetailsQuery.data.firstName?.[0]}
                        {memberDetailsQuery.data.lastName?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h2 className="text-lg font-bold">{memberDetailsQuery.data.fullNames}</h2>
                      <p className="text-sm text-muted-foreground">{memberDetailsQuery.data.phone}</p>
                    </div>
                  </div>
                </div>

                {/* Tabs for organized content */}
                <Tabs defaultValue="overview" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="contributions">History ({filteredMemberContributionHistory.length + filteredMemberLoanHistory.length})</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="mt-4">
                    <div className="bg-card rounded-lg border overflow-hidden">
                      <table className="w-full text-sm">
                        <tbody>
                          {[
                            ["Gender", memberDetailsQuery.data.gender || "N/A"],
                            ["Marriage Status", memberDetailsQuery.data.marriageStatus || "N/A"],
                            ["Source of Income", memberDetailsQuery.data.sourceOfIncome || "N/A"],
                            ["Joined", memberDetailsQuery.data.joinedAt ? new Date(memberDetailsQuery.data.joinedAt).toLocaleDateString() : "N/A"],
                            ["Country", memberDetailsQuery.data.country || "N/A"],
                            ["Zone", memberDetailsQuery.data.branch?.name || "N/A"],
                            ["Address", memberDetailsQuery.data.currentAddress || "N/A"],
                            [
                              "Leader Roles",
                              memberDetailsQuery.data.leaderRoles?.length > 0
                                ? memberDetailsQuery.data.leaderRoles
                                  .map((leaderRole) => `${leaderRole.role}${leaderRole.group?.name ? ` in ${leaderRole.group.name}` : ""}`)
                                  .join(", ")
                                : "No leader roles",
                            ],
                          ].map(([label, value]) => (
                            <tr key={label} className="border-b last:border-b-0">
                              <th className="w-48 bg-muted/40 px-3 py-2 text-left font-medium text-muted-foreground">
                                {label}
                              </th>
                              <td className="px-3 py-2 font-medium text-gray-900">
                                {value}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </TabsContent>

                  <TabsContent value="contributions" className="mt-4">
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-gray-700">Year</label>
                          <Select value={memberHistoryYear} onValueChange={setMemberHistoryYear}>
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="All Years" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Years</SelectItem>
                              {memberAvailableHistoryYears.map((year) => (
                                <SelectItem key={year} value={year.toString()}>
                                  {year}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-gray-700">Month</label>
                          <Select value={memberHistoryMonth} onValueChange={setMemberHistoryMonth}>
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="All Months" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Months</SelectItem>
                              {memberHistoryMonths.map((month) => (
                                <SelectItem key={month.value} value={month.value}>
                                  {month.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6">
                        <div className="bg-card rounded-lg border p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-muted-foreground">Contributions</p>
                              <p className="text-base font-bold">{formatCurrency(memberContributionTotal)}</p>
                            </div>
                            <Wallet className="h-5 w-5 text-[#0A9F43]" />
                          </div>
                        </div>
                        <div className="bg-card rounded-lg border p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-muted-foreground">Contribution Records</p>
                              <p className="text-base font-bold">{filteredMemberContributionHistory.length}</p>
                            </div>
                            <Receipt className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="bg-card rounded-lg border p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-muted-foreground">Verified</p>
                              <p className="text-base font-bold">{memberContributionVerified}</p>
                            </div>
                            <ShieldCheck className="h-5 w-5 text-emerald-600" />
                          </div>
                        </div>
                        <div className="bg-card rounded-lg border p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-muted-foreground">Loans</p>
                              <p className="text-base font-bold">{formatCurrency(memberLoanTotal)}</p>
                            </div>
                            <Landmark className="h-5 w-5 text-indigo-600" />
                          </div>
                        </div>
                        <div className="bg-card rounded-lg border p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-muted-foreground">Loan Paid</p>
                              <p className="text-base font-bold">{formatCurrency(memberLoanPaid)}</p>
                            </div>
                            <ShieldCheck className="h-5 w-5 text-sky-600" />
                          </div>
                        </div>
                        <div className="bg-card rounded-lg border p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-muted-foreground">Loan Balance</p>
                              <p className="text-base font-bold">{formatCurrency(memberLoanBalance)}</p>
                            </div>
                            <Landmark className="h-5 w-5 text-purple-600" />
                          </div>
                        </div>
                      </div>

                      <div className="bg-card rounded-lg border overflow-hidden">
                        <div className="flex items-center justify-between border-b px-3 py-2">
                          <div>
                            <h3 className="text-sm font-semibold">Contribution History</h3>
                            <p className="text-[11px] text-muted-foreground">View all verified and pending contributions made by this member.</p>
                          </div>
                          {memberContributionsQuery.isFetching && (
                            <Loader className="h-4 w-4 animate-spin text-muted-foreground" />
                          )}
                        </div>
                        {memberContributionsQuery.isLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader className="h-5 w-5 animate-spin" />
                          </div>
                        ) : filteredMemberContributionHistory.length === 0 ? (
                          <div className="py-8 text-center text-sm text-muted-foreground">
                            No contribution records found.
                          </div>
                        ) : (
                          <div className="max-h-[320px] overflow-auto">
                            <table className="w-full text-sm">
                              <thead className="sticky top-0 bg-muted/60">
                                <tr className="border-b">
                                  <th className="px-3 py-2 text-left font-medium">Date</th>
                                  <th className="px-3 py-2 text-left font-medium">Type</th>
                                  <th className="px-3 py-2 text-left font-medium">Method</th>
                                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                                  <th className="px-3 py-2 text-right font-medium">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredMemberContributionHistory.map((contribution: any, index: number) => {
                                  const dateValue = contribution?.salaryMonth || contribution?.contributionDate || contribution?.createdAt;
                                  const status = contribution?.status || (contribution?.isVerified ? "Verified" : "Pending");
                                  const amount = Number(contribution?.amount || contribution?.depositAmount || 0);

                                  return (
                                    <tr key={contribution?.id || index} className="border-b last:border-b-0">
                                      <td className="px-3 py-2">
                                        <div className="flex items-center gap-2">
                                          <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                                          <span>{dateValue ? new Date(dateValue).toLocaleDateString() : "N/A"}</span>
                                        </div>
                                      </td>
                                      <td className="px-3 py-2">{contribution?.contributionType || "Contribution"}</td>
                                      <td className="px-3 py-2">{contribution?.paymentMethod || "N/A"}</td>
                                      <td className="px-3 py-2 text-right font-semibold">{formatCurrency(amount)}</td>
                                      <td className="px-3 py-2 text-right">
                                        <Badge variant="outline" className={cn(
                                          "text-xs",
                                          status === "Verified" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-orange-200 bg-orange-50 text-orange-700"
                                        )}>
                                          {status}
                                        </Badge>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      <div className="bg-card rounded-lg border overflow-hidden">
                        <div className="flex items-center justify-between border-b px-3 py-2">
                          <div>
                            <h3 className="text-sm font-semibold">Loan History</h3>
                            <p className="text-[11px] text-muted-foreground">View loans, repayments, paid amount, and remaining balance for this member.</p>
                          </div>
                          {memberLoansQuery.isFetching && (
                            <Loader className="h-4 w-4 animate-spin text-muted-foreground" />
                          )}
                        </div>
                        {memberLoansQuery.isLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader className="h-5 w-5 animate-spin" />
                          </div>
                        ) : filteredMemberLoanHistory.length === 0 ? (
                          <div className="py-8 text-center text-sm text-muted-foreground">
                            No loan records found.
                          </div>
                        ) : (
                          <div className="max-h-[320px] overflow-auto">
                            <table className="w-full text-sm">
                              <thead className="sticky top-0 bg-muted/60">
                                <tr className="border-b">
                                  <th className="px-3 py-2 text-left font-medium">Date</th>
                                  <th className="px-3 py-2 text-left font-medium">Type</th>
                                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                                  <th className="px-3 py-2 text-right font-medium">Paid</th>
                                  <th className="px-3 py-2 text-right font-medium">Balance</th>
                                  <th className="px-3 py-2 text-right font-medium">Status</th>
                                  <th className="px-3 py-2 text-center font-medium">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredMemberLoanHistory.map((loan: any, index: number) => {
                                  const dateValue = loan?.applicationDate || loan?.createdAt || loan?.date;
                                  const amount = Number(loan?.amount || 0);
                                  const paid = Number(loan?.totalPaid || loan?.paidAmount || 0);
                                  const balance = Number(loan?.dueAmount ?? Math.max(amount - paid, 0));
                                  const status = loan?.status || "Pending";
                                  const statusNorm = String(status).toLowerCase().replace(/\s+/g, "_");
                                  const canPay = ["active", "disbursed", "overdue", "approved"].includes(statusNorm);

                                  return (
                                    <tr key={loan?.id || index} className="border-b last:border-b-0">
                                      <td className="px-3 py-2">
                                        <div className="flex items-center gap-2">
                                          <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                                          <span>{dateValue ? new Date(dateValue).toLocaleDateString() : "N/A"}</span>
                                        </div>
                                      </td>
                                      <td className="px-3 py-2">{loan?.loanType || loan?.type || "Loan"}</td>
                                      <td className="px-3 py-2 text-right font-semibold">{formatCurrency(amount)}</td>
                                      <td className="px-3 py-2 text-right">{formatCurrency(paid)}</td>
                                      <td className="px-3 py-2 text-right">{formatCurrency(balance)}</td>
                                      <td className="px-3 py-2 text-right">
                                        <Badge variant="outline" className={cn(
                                          "text-xs",
                                          statusNorm === "completed" || statusNorm === "fully_repaid"
                                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                            : "border-blue-200 bg-blue-50 text-blue-700"
                                        )}>
                                          {status}
                                        </Badge>
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                          {canPay && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="h-7 px-2 text-[11px] text-green-600 hover:text-green-700 hover:bg-green-50 gap-1"
                                              onClick={() => setRecordPaymentLoan(loan)}
                                            >
                                              <DollarSign className="h-3 w-3" />
                                              Pay
                                            </Button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>


                    </div>
                  </TabsContent>

                </Tabs>
              </div>
            </ScrollArea>
          ) : (
            <div className="p-6 text-center text-muted-foreground h-full flex items-center justify-center">
              <div>
                <p className="mb-2">No member details available</p>
                <Button variant="outline" size="sm" onClick={() => setIsViewDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}

          {/* Record Payment Modal */}
          {recordPaymentLoan && (() => {
            const member = memberDetailsQuery.data;
            const loanWithMember = {
              ...recordPaymentLoan,
              memberName: recordPaymentLoan.memberName || member?.fullNames || "",
              memberCode: recordPaymentLoan.memberCode || member?.memberCode || "",
              memberPhone: recordPaymentLoan.memberPhone || member?.phone || "",
              loanType: recordPaymentLoan.loanType || recordPaymentLoan.type || "Loan",
            };
            return (
              <PaymentModal
                open={!!recordPaymentLoan}
                onOpenChange={(open) => { if (!open) setRecordPaymentLoan(null); }}
                loan={loanWithMember}
                mode="admin"
                onSubmit={(data) => recordPaymentMutation.mutate({ loanId: recordPaymentLoan.id, payload: data })}
                isLoading={recordPaymentMutation.isLoading}
              />
            );
          })()}
        </DialogContent>
      </Dialog>

      <div className="px-4 py-2 w-full max-w-full">
        {/* Header with Title, Meta and Action Buttons */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-gray-900">Members Management</h1>
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#0A9F43]/10 text-[#0A9F43] whitespace-nowrap">{totalMatchingMembers} total</span>
            </div>
            <p className="text-xs text-gray-400">Manage member records, savings, and participation</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Print Button */}
            <motion.button
              className="h-7 rounded-md px-2 text-[11px] font-semibold flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex-shrink-0"
              style={{
                backgroundColor: "#F8FAFC",
                border: "1px solid #CBD5E1",
                color: "#334155",
              }}
              whileHover={{ scale: 1.05, translateY: -1, transition: { duration: 0.2 } }}
              whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
              onClick={async () => {
                // Fetch ALL members for printing (respecting current filters and search)
                const fieldMap = {
                  branch: "branchId",
                  membershipStatus: "membershipStatus",
                  createdAt: "createdAt",
                } as Record<string, string>;

                const apiFilters = columnFilters
                  .filter((f) => f.id !== "role")
                  .map((filter) => {
                    if (filter.id === "createdAt" && filter.value?.length === 2) {
                      const [startDate, endDate] = filter.value;
                      if (!startDate || !endDate) {
                        return null;
                      }
                      const endOfDay = new Date(endDate);
                      endOfDay.setHours(23, 59, 59, 999);
                      return {
                        field: fieldMap[filter.id] || filter.id,
                        operator: "between" as const,
                        value: [
                          new Date(startDate).toISOString(),
                          endOfDay.toISOString(),
                        ],
                      };
                    }
                    if (!Array.isArray(filter.value) || filter.value.length === 0) {
                      return null;
                    }
                    const normalizedValues = filter.value
                      .map((v) => (typeof v === "object" && v !== null ? v.value : v))
                      .map((v) => {
                        if (filter.id === "branch") {
                          const numericValue = Number(v);
                          return Number.isNaN(numericValue) ? v : numericValue;
                        }
                        return v;
                      })
                      .filter((v) => v !== undefined && v !== null);
                    if (normalizedValues.length === 0) {
                      return null;
                    }
                    return {
                      field: fieldMap[filter.id] || filter.id,
                      operator: "in" as const,
                      value: normalizedValues,
                    };
                  })
                  .filter(Boolean);

                const params: Record<string, any> = {
                  limit: 10000, // Fetch all members for printing
                  page: 1,
                  ...(searchText && { search: searchText }),
                  ...(apiFilters.length > 0 && { filters: JSON.stringify(apiFilters) }),
                  sortBy: sorting[0]?.id || "createdAt",
                  order: sorting[0]?.desc ? "DESC" : "ASC",
                };

                const { data: allMembersData } = await api.get(`/members`, { params });
                const allMembers = allMembersData?.results?.map((e: any) => ({
                  ...e,
                  branchName: e?.branch?.name || "",
                  branchId: e?.branch?.id,
                  group: e?.groupMemberships && e.groupMemberships.length > 0 ? e.groupMemberships[0]?.group?.name : "",
                  createdAt: e?.createdAt,
                  leaderRoles: e?.leaderRoles || [],
                  groupMemberships: e?.groupMemberships || [],
                  contributions: e?.contributions || [],
                  loans: e?.loans || [],
                  totalSharesBought: e?.totalSharesBought || 0,
                  sharesTotalAmount: e?.savingAmount ?? e?.sharesTotalAmount ?? 0,
                  unpaidLoansAndFines: e?.unpaidLoanAndFines ?? e?.unpaidLoansAndFines ?? 0,
                  fullNames: e?.fullNames || `${e?.firstName || ''} ${e?.lastName || ''}`.trim(),
                  currentSavings: e?.savingAmount ?? e?.currentSavings ?? 0,
                  monthlyContribution: e?.monthlyContribution || 0,
                  totalSavings: e?.savingAmount ?? e?.totalSavings ?? 0,
                  totalShares: getShares(e?.totalShares, e?.savingAmount ?? e?.currentSavings ?? e?.totalSavings),
                  monthsSaved: e?.monthsSaved || 0,
                  activeLoanBalance: e?.unpaidLoanAndFines ?? e?.activeLoanBalance ?? 0,
                  outstandingFines: e?.outstandingFines || 0,
                  status: e?.status || e?.membershipStatus || "Active",
                  financialMetrics: e?.financialMetrics || {},
                })) || [];

                const data = allMembers;
                const startDate = dateRange?.from ? format(dateRange.from, 'MMMM d, yyyy') : format(new Date(), 'MMMM d, yyyy');
                const endDate = dateRange?.to ? format(dateRange.to, 'MMMM d, yyyy') : format(new Date(), 'MMMM d, yyyy');

                // Create print content
                const printContent = `
                  <div class="print-header">
                    <div class="logo-container">
                      <div class="logo-box">
                        <img src={getCooperativeLogoSrc()} alt="IKIMINA Logo" />
                      </div>
                    </div>
                    <div class="company-info">
                      <div class="company-name">CAISSE D'ENTRAIDE IPRC KARONGI</div>
                      <div class="report-title">Members Report</div>
                      <div class="date-range">${startDate} - ${endDate}</div>
                    </div>
                  </div>
                  <hr class="separator-thick">
                  <div class="contact-info">
                    <div><strong>Address:</strong> Karongi, Rwanda</div>
                    <div><strong>Phone:</strong> 0793502827</div>
                    <div><strong>Email:</strong> caisse.iprcwest@gmail.com</div>
                  </div>
                  <hr class="separator-thin">
                  
                  <div style="text-align: center; font-size: 20px; font-weight: bold; margin: 20px 0; color: #050500;">
                    MEMBERS REPORT
                  </div>
                  
                  <table>
                    <thead>
                      <tr>
                        <th>Full Name</th>
                        <th>Contact</th>
                        <th>Join Date</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Months Saved</th>
                        <th>Total Shares</th>
                        <th>Total Savings</th>
                        <th>Loan Balance</th>
                        <th>Outstanding Fines</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${data.map(m => {
                  const fullName = m.fullNames || `${m.firstName || ''} ${m.lastName || ''}`.trim() || 'N/A';
                  const primaryRole = m.leaderRoles?.[0]?.role || m.leadershipRole || m.role || 'Member';
                  const status = m.status || m.membershipStatus || 'Active';

                  return `
                        <tr>
                          <td>${fullName}</td>
                          <td>${m.phone || 'N/A'}</td>
                          <td>${m.joinedAt ? format(new Date(m.joinedAt), 'MMM d, yyyy') : (m.createdAt ? format(new Date(m.createdAt), 'MMM d, yyyy') : 'N/A')}</td>
                          <td>${primaryRole}</td>
                          <td>${status}</td>
                          <td>${m.monthsSaved || 0}</td>
                          <td>${Number(m.totalShares || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                          <td>${(m.totalSavings || 0).toLocaleString()} FRW</td>
                          <td>${(m.activeLoanBalance || 0).toLocaleString()} FRW</td>
                          <td>${(m.outstandingFines || 0).toLocaleString()} FRW</td>
                        </tr>
                      `;
                }).join('')}
                    </tbody>
                  </table>
                  
                  <div class="summary">
                    <strong>Total Members:</strong> ${data.length} | 
                    <strong>Generated on:</strong> ${format(new Date(), 'MMMM d, yyyy')} at ${format(new Date(), 'h:mm a')}
                  </div>
                `;

                // Create print styles
                const printStyles = `
                  <style id="print-styles">
                    #print-container {
                      position: fixed;
                      left: 0;
                      top: 0;
                      width: 100%;
                      height: 100%;
                      background: white;
                      padding: 20px;
                      z-index: 99999;
                      overflow: auto;
                      box-sizing: border-box;
                    }
                    #print-container .print-header {
                      display: flex;
                      align-items: center;
                      margin-bottom: 15px;
                    }
                    #print-container .logo-container {
                      margin-right: 20px;
                    }
                    #print-container .logo-box {
                      width: 120px;
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                    }
                    #print-container .logo-box img {
                      max-width: 120px;
                      height: auto;
                      object-fit: contain;
                      border-radius: 50%;
                    }
                    #print-container .company-info {
                      flex: 1;
                    }
                    #print-container .company-name {
                      font-size: 24px;
                      font-weight: bold;
                      color: #000;
                      margin-bottom: 5px;
                    }
                    #print-container .report-title {
                      font-size: 18px;
                      font-weight: bold;
                      color: #000;
                      margin-bottom: 5px;
                    }
                    #print-container .date-range {
                      font-size: 14px;
                      color: #000;
                      margin-bottom: 0;
                    }
                    #print-container .separator-thick {
                      border: none;
                      border-top: 3px solid #000;
                      margin: 15px 0;
                      width: 100%;
                    }
                    #print-container .separator-thin {
                      border: none;
                      border-top: 1px solid #ccc;
                      margin: 15px 0 20px 0;
                      width: 100%;
                    }
                    #print-container .contact-info {
                      display: block;
                      font-size: 12px;
                      color: #000;
                      margin-bottom: 15px;
                    }
                    #print-container .contact-info div {
                      display: block;
                      margin-bottom: 5px;
                    }
                    #print-container table {
                      width: 100%;
                      border-collapse: collapse;
                      margin-top: 20px;
                      font-size: 11px;
                    }
                    #print-container th {
                      background-color: #4dbd07 !important;
                      color: white !important;
                      padding: 10px 6px;
                      text-align: left;
                      font-weight: 600;
                      border: 1px solid #3d9a06;
                    }
                    #print-container td {
                      border: 1px solid #ddd;
                      padding: 8px 6px;
                      text-align: left;
                    }
                    #print-container tbody tr:nth-child(even) {
                      background-color: #f9f9f9;
                    }
                    @media print {
                      @page {
                        margin: 1cm;
                        size: A4;
                      }
                      body * {
                        visibility: hidden;
                      }
                      #print-container,
                      #print-container * {
                        visibility: visible;
                      }
                      #print-container {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        height: auto;
                        overflow: visible;
                      }
                      #print-container th {
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                      }
                    }
                  </style>
                `;

                try {
                  // Remove existing print container if any
                  const existingContainer = document.getElementById('print-container');
                  if (existingContainer) {
                    existingContainer.remove();
                  }

                  // Remove existing print styles if any
                  const existingStyles = document.getElementById('print-styles');
                  if (existingStyles) {
                    existingStyles.remove();
                  }

                  // Load and convert logo to base64 to avoid reload delays
                  const getBase64Image = (): Promise<string> => {
                    return new Promise((resolve) => {
                      const img = new Image();
                      img.crossOrigin = 'anonymous';
                      img.onload = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');
                        ctx?.drawImage(img, 0, 0);
                        const dataURL = canvas.toDataURL('image/jpeg');
                        resolve(dataURL);
                      };
                      img.onerror = () => resolve(getCooperativeLogoSrc()); // Fallback to URL
                      img.src = getCooperativeLogoSrc();
                    });
                  };

                  const logoBase64 = await getBase64Image();

                  // Replace logo URL with base64 in print content
                  const printContentWithBase64 = printContent.replace(
                    /src="\/images\/ikimina-logo\.jpg"/g,
                    `src="${logoBase64}"`
                  );

                  // Add print styles to head
                  document.head.insertAdjacentHTML('beforeend', printStyles);

                  // Create print container
                  const printContainer = document.createElement('div');
                  printContainer.id = 'print-container';
                  printContainer.className = 'print-container';
                  printContainer.innerHTML = printContentWithBase64;
                  document.body.appendChild(printContainer);

                  // Wait for content to render, then trigger print
                  setTimeout(() => {
                    window.print();

                    // Clean up after printing
                    setTimeout(() => {
                      if (printContainer && printContainer.parentNode) {
                        printContainer.remove();
                      }
                      const styles = document.getElementById('print-styles');
                      if (styles) {
                        styles.remove();
                      }
                    }, 500);
                  }, 300);
                } catch (error) {
                  console.error("Print error:", error);
                  toast.error("Failed to print report");
                }
              }}
            >
              <motion.span
                className="flex items-center text-gray-700"
                initial={{ x: 0 }}
                whileHover={{ x: 2 }}
                transition={{ duration: 0.2 }}
              >
                <Printer className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 mr-1 sm:mr-1.5 md:mr-2" />
                Print
              </motion.span>
            </motion.button>

            {/* Bulk Import Button */}
            <motion.button
              onClick={() => setBulkImportOpen(true)}
              className="h-7 rounded-md px-2 text-[11px] font-semibold flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex-shrink-0"
              style={{
                backgroundColor: "#F0FDF4",
                border: "1px solid #86EFAC",
                color: "#16A34A",
              }}
              whileHover={{ scale: 1.05, translateY: -1, transition: { duration: 0.2 } }}
              whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
            >
              <motion.span
                className="flex items-center"
                initial={{ x: 0 }}
                whileHover={{ x: 2 }}
                transition={{ duration: 0.2 }}
              >
                <Upload className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 mr-1 sm:mr-1.5 md:mr-2" style={{ color: "#16A34A" }} />
                <span style={{ color: "#16A34A" }}>Upload</span>
              </motion.span>
            </motion.button>

            {/* Add Member Button */}
            <motion.button
              onClick={() => newRecordModal.setisOpen(true)}
              className="h-7 rounded-md px-2.5 text-[11px] font-semibold flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex-shrink-0 shadow-sm"
              style={{
                backgroundColor: '#007A3D',
                border: 'none',
                color: 'white'
              }}
              whileHover={{ scale: 1.05, translateY: -1, boxShadow: "0 4px 12px rgba(0,122,61,0.3)", transition: { duration: 0.2 } }}
              whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
            >
              <motion.span
                className="flex items-center text-white"
                initial={{ x: 0 }}
                whileHover={{ x: 2 }}
                transition={{ duration: 0.2 }}
              >
                <PlusCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 mr-1 sm:mr-1.5 md:mr-2" />
                <span>Add Member</span>
              </motion.span>
            </motion.button>
          </div>
        </div>

        {/* Secondary Actions Row - Date Picker and Download Report */}
        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "justify-start text-left font-normal h-7 px-2 text-xs",
                  !dateRange && "text-muted-foreground"
                )}
                style={{
                  backgroundColor: dateRange && dateRange.from ? "#EAF7F0" : "transparent",
                  borderColor: "#B7E4C7",
                  color: "#007A3D",
                }}
              >
                <CalendarIcon className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">
                  {dateRange?.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "LLL dd, y")} -{" "}
                        {format(dateRange.to, "LLL dd, y")}
                      </>
                    ) : (
                      format(dateRange.from, "LLL dd, y")
                    )
                  ) : (
                    "Pick a date range"
                  )}
                </span>
                <span className="sm:hidden">Date</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={handleDateRangeChange}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            style={{
              backgroundColor: "#EFF6FF",
              borderColor: "#BFDBFE",
              color: "#2563EB",
            }}
            onClick={async () => {
              // Dynamically import jsPDF and html2canvas
              const jsPDF = (await import('jspdf')).jsPDF;
              const html2canvas = (await import('html2canvas')).default;

              // Fetch ALL members for PDF export (respecting current filters and search)
              const fieldMap = {
                branch: "branchId",
                membershipStatus: "membershipStatus",
                createdAt: "createdAt",
              } as Record<string, string>;

              const apiFilters = columnFilters
                .filter((f) => f.id !== "role")
                .map((filter) => {
                  if (filter.id === "createdAt" && filter.value?.length === 2) {
                    const [startDate, endDate] = filter.value;
                    if (!startDate || !endDate) {
                      return null;
                    }
                    const endOfDay = new Date(endDate);
                    endOfDay.setHours(23, 59, 59, 999);
                    return {
                      field: fieldMap[filter.id] || filter.id,
                      operator: "between" as const,
                      value: [
                        new Date(startDate).toISOString(),
                        endOfDay.toISOString(),
                      ],
                    };
                  }
                  if (!Array.isArray(filter.value) || filter.value.length === 0) {
                    return null;
                  }
                  const normalizedValues = filter.value
                    .map((v) => (typeof v === "object" && v !== null ? v.value : v))
                    .map((v) => {
                      if (filter.id === "branch") {
                        const numericValue = Number(v);
                        return Number.isNaN(numericValue) ? v : numericValue;
                      }
                      return v;
                    })
                    .filter((v) => v !== undefined && v !== null);
                  if (normalizedValues.length === 0) {
                    return null;
                  }
                  return {
                    field: fieldMap[filter.id] || filter.id,
                    operator: "in" as const,
                    value: normalizedValues,
                  };
                })
                .filter(Boolean);

              const params: Record<string, any> = {
                limit: 10000, // Fetch all members for PDF
                page: 1,
                ...(searchText && { search: searchText }),
                ...(apiFilters.length > 0 && { filters: JSON.stringify(apiFilters) }),
                sortBy: sorting[0]?.id || "createdAt",
                order: sorting[0]?.desc ? "DESC" : "ASC",
              };

              const { data: allMembersData } = await api.get(`/members`, { params });
              const allMembers = allMembersData?.results?.map((e: any) => ({
                ...e,
                branchName: e?.branch?.name || "",
                branchId: e?.branch?.id,
                group: e?.groupMemberships && e.groupMemberships.length > 0 ? e.groupMemberships[0]?.group?.name : "",
                createdAt: e?.createdAt,
                leaderRoles: e?.leaderRoles || [],
                groupMemberships: e?.groupMemberships || [],
                contributions: e?.contributions || [],
                loans: e?.loans || [],
                totalSharesBought: e?.totalSharesBought || 0,
                sharesTotalAmount: e?.savingAmount ?? e?.sharesTotalAmount ?? 0,
                unpaidLoansAndFines: e?.unpaidLoanAndFines ?? e?.unpaidLoansAndFines ?? 0,
                fullNames: e?.fullNames || `${e?.firstName || ''} ${e?.lastName || ''}`.trim(),
                currentSavings: e?.savingAmount ?? e?.currentSavings ?? 0,
                monthlyContribution: e?.monthlyContribution || 0,
                totalSavings: e?.savingAmount ?? e?.totalSavings ?? 0,
                financialMetrics: e?.financialMetrics || {},
              })) || [];

              const data = allMembers;
              const startDate = dateRange?.from ? format(dateRange.from, 'MMMM d, yyyy') : format(new Date(), 'MMMM d, yyyy');
              const endDate = dateRange?.to ? format(dateRange.to, 'MMMM d, yyyy') : format(new Date(), 'MMMM d, yyyy');

              // Create a hidden div for PDF content
              const pdfDiv = document.createElement('div');
              pdfDiv.style.position = 'fixed';
              pdfDiv.style.left = '-9999px';
              pdfDiv.style.top = '0';
              pdfDiv.style.width = '1200px';
              pdfDiv.style.background = '#fff';
              pdfDiv.style.fontFamily = 'Arial, sans-serif';
              pdfDiv.style.padding = '40px';
              pdfDiv.innerHTML = `
                  <div style="margin-bottom: 30px;">
                    <div style="display: flex; align-items: center; margin-bottom: 20px;">
                      <div style="width: 60px; height: 60px; background: linear-gradient(135deg, #4dbd07 0%, #3d9a06 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 24px; margin-right: 15px;">
                        MS
                      </div>
                      <div style="flex: 1;">
                        <div style="font-size: 24px; font-weight: bold; color: #050500; margin-bottom: 5px;">CAISSE D'ENTRAIDE IPRC KARONGI</div>
                        <div style="font-size: 18px; color: #666; margin-bottom: 10px;">Members Report</div>
                        <div style="font-size: 14px; color: #666; margin-bottom: 15px;">${startDate} - ${endDate}</div>
                        <div style="display: flex; flex-wrap: wrap; gap: 20px; font-size: 12px; color: #666;">
                          <div><strong>Address:</strong> Karongi, Rwanda</div>
                          <div><strong>Phone:</strong> +250 788 000 000</div>
                          <div><strong>Email:</strong> info@ikimina.rw</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div style="text-align: center; font-size: 20px; font-weight: bold; margin: 20px 0; color: #050500;">
                    MEMBERS LIST
                  </div>
                  
                  <table style="width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 20px;">
                    <thead>
                      <tr>
                        <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Full Name</th>
                        <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Email</th>
                        <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Contact</th>
                        <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Join Date</th>
                        <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Role</th>
                        <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Monthly Contribution</th>
                        <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Total Savings</th>
                        <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Shares Bought</th>
                        <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Shares (Total Amount)</th>
                        <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Unpaid Loan and Fines</th>
                        <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Marriage Status</th>
                        <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Active Loans</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${data.map((m, index) => {
                const fullName = m.fullNames || `${m.firstName || ''} ${m.lastName || ''}`.trim() || 'N/A';
                const primaryRole = m.leaderRoles?.[0]?.role || m.leadershipRole || m.role || 'Member';
                const activeLoans = m.financialMetrics?.activeLoansCount ||
                  (m.loans ? m.loans.filter((l: any) => ["Active", "Defaulted", "Approved"].includes(l.status)).length : 0);
                const joinDate = m.joinedAt ? format(new Date(m.joinedAt), 'MMM d, yyyy') : (m.createdAt ? format(new Date(m.createdAt), 'MMM d, yyyy') : 'N/A');

                return `
                        <tr style="${index % 2 === 0 ? 'background-color: #f9f9f9;' : ''}">
                          <td style="border: 1px solid #ddd; padding: 8px 6px;">${fullName}</td>
                          <td style="border: 1px solid #ddd; padding: 8px 6px;">${m.email || 'N/A'}</td>
                          <td style="border: 1px solid #ddd; padding: 8px 6px;">${m.phone || 'N/A'}</td>
                          <td style="border: 1px solid #ddd; padding: 8px 6px;">${joinDate}</td>
                          <td style="border: 1px solid #ddd; padding: 8px 6px;">${primaryRole}</td>
                          <td style="border: 1px solid #ddd; padding: 8px 6px;">${(m.monthlyContribution || 0).toLocaleString()} FRW</td>
                          <td style="border: 1px solid #ddd; padding: 8px 6px;">${(m.totalSavings || 0).toLocaleString()} FRW</td>
                          <td style="border: 1px solid #ddd; padding: 8px 6px;">${(m.totalSharesBought || 0).toLocaleString()}</td>
                          <td style="border: 1px solid #ddd; padding: 8px 6px;">${(m.sharesTotalAmount || 0).toLocaleString()} FRW</td>
                          <td style="border: 1px solid #ddd; padding: 8px 6px;">${(m.unpaidLoansAndFines || 0).toLocaleString()} FRW</td>
                          <td style="border: 1px solid #ddd; padding: 8px 6px;">${m.marriageStatus || 'N/A'}</td>
                          <td style="border: 1px solid #ddd; padding: 8px 6px;">${activeLoans}</td>
                        </tr>
                      `;
              }).join('')}
                    </tbody>
                  </table>
                  
                  <div style="margin-top: 24px; padding: 15px; background-color: #f5f5f5; border-radius: 5px; font-size: 14px;">
                    <strong>Total Members:</strong> ${data.length} | 
                    <strong>Generated on:</strong> ${format(new Date(), 'MMMM d, yyyy')} at ${format(new Date(), 'h:mm a')}
                  </div>
                `;
              document.body.appendChild(pdfDiv);
              // Use html2canvas to render the div
              const canvas = await html2canvas(pdfDiv, { scale: 2 });
              const imgData = canvas.toDataURL('image/png');
              const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [canvas.width, canvas.height] });
              pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
              pdf.save('members_report.pdf');
              document.body.removeChild(pdfDiv);
            }}
          >
            <span className="hidden sm:inline">Download Members Report</span>
            <span className="sm:hidden">Report</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            style={{
              backgroundColor: "#ECFDF5",
              borderColor: "#A7F3D0",
              color: "#059669",
            }}
            onClick={async () => {
              try {
                const fieldMap = {
                  branch: "branchId",
                  membershipStatus: "membershipStatus",
                  createdAt: "createdAt",
                } as Record<string, string>;

                const apiFilters = columnFilters
                  .filter((f) => f.id !== "role")
                  .map((filter) => {
                    if (filter.id === "createdAt" && filter.value?.length === 2) {
                      const [startDate, endDate] = filter.value;
                      if (!startDate || !endDate) return null;
                      const endOfDay = new Date(endDate);
                      endOfDay.setHours(23, 59, 59, 999);
                      return {
                        field: fieldMap[filter.id] || filter.id,
                        operator: "between" as const,
                        value: [
                          new Date(startDate).toISOString(),
                          endOfDay.toISOString(),
                        ],
                      };
                    }
                    if (!Array.isArray(filter.value) || filter.value.length === 0) {
                      return null;
                    }
                    const normalizedValues = filter.value
                      .map((v) => (typeof v === "object" && v !== null ? v.value : v))
                      .map((v) => {
                        if (filter.id === "branch") {
                          const numericValue = Number(v);
                          return Number.isNaN(numericValue) ? v : numericValue;
                        }
                        return v;
                      })
                      .filter((v) => v !== undefined && v !== null);
                    if (normalizedValues.length === 0) return null;
                    return {
                      field: fieldMap[filter.id] || filter.id,
                      operator: "in" as const,
                      value: normalizedValues,
                    };
                  })
                  .filter(Boolean);

                const params = new URLSearchParams({
                  format: "excel",
                  limit: "10000",
                  page: "1",
                  sortBy: sorting[0]?.id || "createdAt",
                  order: sorting[0]?.desc ? "DESC" : "ASC",
                });
                if (searchText) params.set("search", searchText);
                if (apiFilters.length > 0) {
                  params.set("filters", JSON.stringify(apiFilters));
                }

                const response = await api.get(`/members/export?${params}`, {
                  responseType: "blob",
                });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement("a");
                link.href = url;
                link.download = `members-export-${new Date().toISOString().split("T")[0]}.xlsx`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success("Excel file downloaded");
              } catch (error) {
                console.error("Excel export error:", error);
                toast.error("Failed to download Excel export");
              }
            }}
          >
            <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5 sm:h-4 sm:w-4 inline" />
            <span className="hidden sm:inline">Download Excel (.xlsx)</span>
            <span className="sm:hidden">Excel</span>
          </Button>
        </div>

        {/* KPI Cards — 4 compact cards in a single row */}
        {statsFailed && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            Unable to load member statistics. Showing placeholder values.
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2.5 mb-3">
          <Card className="relative overflow-hidden border border-gray-100 bg-white shadow-sm">
            <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-[#0A9F43] to-[#12B24B]" />
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-gray-900">Total Members</p>
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0A9F43]/10 text-[#0A9F43]">
                  <Users className="h-3.5 w-3.5" />
                </div>
              </div>
              <p className="text-lg font-bold tracking-tight text-gray-900">{statsQuery.isLoading ? <span className="text-gray-300">—</span> : stats.totalMembers}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Registered members</p>
            </CardContent>
          </Card>
          <Card className="relative overflow-hidden border border-gray-100 bg-white shadow-sm">
            <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-400" />
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-gray-900">Total Savings</p>
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                  <Landmark className="h-3.5 w-3.5" />
                </div>
              </div>
              <p className="text-lg font-bold tracking-tight text-gray-900">{statsQuery.isLoading ? <span className="text-gray-300">—</span> : formatCurrency(stats.totalSavings)}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Combined savings</p>
            </CardContent>
          </Card>
          <Card className="relative overflow-hidden border border-gray-100 bg-white shadow-sm">
            <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-orange-400 to-amber-400" />
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-gray-900">Total Contributions</p>
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-600">
                  <AlertCircle className="h-3.5 w-3.5" />
                </div>
              </div>
              <p className="text-lg font-bold tracking-tight text-gray-900">{statsQuery.isLoading ? <span className="text-gray-300">—</span> : formatCurrency(statsTotalContributions)}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Total of all contributions</p>
            </CardContent>
          </Card>
          <Card className="relative overflow-hidden border border-gray-100 bg-white shadow-sm">
            <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-emerald-400 to-green-400" />
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-gray-900">Active Members</p>
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <ShieldCheck className="h-3.5 w-3.5" />
                </div>
              </div>
              <p className="text-lg font-bold tracking-tight text-gray-900">{statsQuery.isLoading ? <span className="text-gray-300">—</span> : stats.activeMembers}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Currently active</p>
            </CardContent>
          </Card>
          <Card className="relative overflow-hidden border border-gray-100 bg-white shadow-sm">
            <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-green-500 to-emerald-400" />
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-gray-900">Total Savings & Contributions</p>
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600">
                  <Wallet className="h-3.5 w-3.5" />
                </div>
              </div>
              <p className="text-lg font-bold tracking-tight text-green-700">{statsQuery.isLoading ? <span className="text-gray-300">—</span> : formatCurrency(statsCombinedTotal || pageCombinedSavingsAndContributions)}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Combined total</p>
            </CardContent>
          </Card>
          <Card className="relative overflow-hidden border border-gray-100 bg-white shadow-sm">
            <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-amber-400 to-orange-400" />
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-gray-900">Total Shares</p>
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <Receipt className="h-3.5 w-3.5" />
                </div>
              </div>
              <p className="text-lg font-bold tracking-tight text-gray-900">{statsQuery.isLoading ? <span className="text-gray-300">—</span> : statsTotalShares.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">Total shares held</p>
            </CardContent>
          </Card>
        </div>

        {/* Summary Widgets — compact row */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-1.5 bg-white border border-gray-100 rounded-lg px-2.5 py-1.5 shadow-sm">
            <span className="text-[10px] font-medium text-blue-600">Male</span>
            <span className="text-xs font-bold text-gray-800">{statsQuery.isLoading ? <span className="text-gray-300">—</span> : stats.maleCount}</span>
          </div>
          <div className="flex items-center gap-1.5 bg-white border border-gray-100 rounded-lg px-2.5 py-1.5 shadow-sm">
            <span className="text-[10px] font-medium text-pink-600">Female</span>
            <span className="text-xs font-bold text-gray-800">{statsQuery.isLoading ? <span className="text-gray-300">—</span> : stats.femaleCount}</span>
          </div>
          <div className="flex items-center gap-1.5 bg-white border border-gray-100 rounded-lg px-2.5 py-1.5 shadow-sm">
            <span className="text-[10px] font-medium text-emerald-600">Active</span>
            <span className="text-xs font-bold text-gray-800">{statsQuery.isLoading ? <span className="text-gray-300">—</span> : stats.activeMembers}</span>
          </div>
          <div className="flex items-center gap-1.5 bg-white border border-gray-100 rounded-lg px-2.5 py-1.5 shadow-sm">
            <span className="text-[10px] font-medium text-indigo-600">New This Month</span>
            <span className="text-xs font-bold text-gray-800">{statsQuery.isLoading ? <span className="text-gray-300">—</span> : stats.newThisMonth}</span>
          </div>
          <div className="flex items-center gap-1.5 bg-white border border-gray-100 rounded-lg px-2.5 py-1.5 shadow-sm">
            <span className="text-[10px] font-medium text-[#0A9F43]">Total Savings</span>
            <span className="text-xs font-bold text-gray-800">{statsQuery.isLoading ? <span className="text-gray-300">—</span> : formatCurrency(stats.totalSavings)}</span>
          </div>
          <div className="flex items-center gap-1.5 bg-white border border-gray-100 rounded-lg px-2.5 py-1.5 shadow-sm">
            <span className="text-[10px] font-medium text-orange-600">Total Loans</span>
            <span className="text-xs font-bold text-gray-800">{statsQuery.isLoading ? <span className="text-gray-300">—</span> : formatCurrency(stats.totalLoans)}</span>
          </div>
        </div>

        <div className="grid grid-cols-1">
          <div className="min-w-0">
            <DataTable
              title=""
              columns={columns}
              data={members}
              facets={[
                {
                  name: "branch",
                  title: "Sector",
                  type: "select",
                  options: branches,
                },
                {
                  name: "role",
                  title: "Leader Role",
                  type: "select",
                  options: [
                    { label: "President", value: "President" },
                    { label: "Secretary", value: "Secretary" },
                    { label: "Accountant", value: "Accountant" },
                    { label: "Member", value: "Member" },
                  ],
                },
                {
                  name: "gender",
                  title: "Gender",
                  type: "select",
                  options: genderOptions,
                },
                {
                  name: "membershipStatus",
                  title: "Status",
                  type: "select",
                  options: [
                    { label: "Active", value: "Active" },
                    { label: "Inactive", value: "Inactive" },
                  ],
                },
              ]}
              isLoading={recordsQuery.status === "loading"}
              defaultColumnVisibility={{
                monthsSaved: true,
                totalShares: true,
                totalSavings: true,
                activeLoanBalance: true,
                membershipStatus: true,
                gender: false,
              }}
              onSearch={(value) => setSearchText(value)}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              setSorting={setSorting}
              sorting={sorting}
              setPagination={setPagination}
              pageIndex={pageIndex}
              pageSize={pageSize}
              pageCount={recordsQuery?.data?.totalPages || 0}
              isFetching={recordsQuery.isFetching}
            />
          </div>
        </div>
      </div>

      <MemberForm
        isOpen={newRecordModal.isOpen || Boolean(recordToEdit)}
        setIsOpen={(e) => {
          newRecordModal.setisOpen(e);
          if (!e) {
            setRecordToEdit(undefined);
          }
        }}
        refetch={recordsQuery.refetch}
        record={recordToEdit}
      />
      {/* Place Bulk Upload Members button on the main actions row, next to Add Member */}
      {/* Removed duplicate Bulk Upload Members button below the table */}
      <BulkImport
        open={bulkImportOpen}
        setOpen={setBulkImportOpen}
        sample={sampleMember}
        name="members"
        endPoint="/members/import"
        onComplete={() => {
          window.location.reload();
        }}
      />
    </>
  );
}
